import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { db, storage } from '../firebase/config';
import { useAuth } from '../contexts/AuthContext';
import {
  collection, doc, getDoc, getDocs, addDoc, updateDoc, deleteDoc,
  query, orderBy, where, limit, serverTimestamp, increment, writeBatch,
} from 'firebase/firestore';
import { DndContext, closestCenter, PointerSensor, KeyboardSensor, useSensor, useSensors } from '@dnd-kit/core';
import {
  SortableContext, verticalListSortingStrategy, useSortable, sortableKeyboardCoordinates,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import DragIndicatorIcon from '@mui/icons-material/DragIndicator';
import {
  Box, Container, Typography, Paper, Button, TextField, IconButton,
  Chip, CircularProgress, Dialog, DialogTitle, DialogContent, DialogActions,
  Grid, MenuItem, Divider, Avatar, Tooltip, Alert, Autocomplete, Switch,
  LinearProgress, FormControl, InputLabel, Select, OutlinedInput, Collapse,
} from '@mui/material';
import { deptCode } from '../utils/depts';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import LagbasLogo from '../components/LagbasLogo';
import EditIcon from '@mui/icons-material/Edit';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import LinkOffIcon from '@mui/icons-material/LinkOff';
import LinkIcon from '@mui/icons-material/Link';
import StarBorderIcon from '@mui/icons-material/StarBorder';
import StarIcon from '@mui/icons-material/Star';
import EmailIcon from '@mui/icons-material/Email';
import PhoneIcon from '@mui/icons-material/Phone';
import SmartphoneIcon from '@mui/icons-material/Smartphone';
import GroupsIcon from '@mui/icons-material/Groups';
import NoteIcon from '@mui/icons-material/Note';
import BusinessIcon from '@mui/icons-material/Business';
import NotificationsActiveIcon from '@mui/icons-material/NotificationsActive';
import CelebrationIcon from '@mui/icons-material/Celebration';
import DescriptionIcon from '@mui/icons-material/Description';
import PersonIcon from '@mui/icons-material/Person';
import EventIcon from '@mui/icons-material/Event';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import StorefrontIcon from '@mui/icons-material/Storefront';
import OpenInNewIcon        from '@mui/icons-material/OpenInNew';
import AttachFileIcon       from '@mui/icons-material/AttachFile';
import InsertDriveFileIcon  from '@mui/icons-material/InsertDriveFile';
import DownloadIcon         from '@mui/icons-material/Download';
import { ref as storageRef, uploadBytesResumable, getDownloadURL, deleteObject } from 'firebase/storage';

import { SWEDISH_MUNICIPALITIES } from '../utils/swedishMunicipalities';
import PlingCelebration from '../components/PlingCelebration';
import PlingMailDialog from '../modules/crm/PlingMailDialog';
import OutlookBookingDialog from '../modules/crm/OutlookBookingDialog';
import FlatboxOfferterSektion from '../modules/crm/FlatboxOfferterSektion';
import OfferMailDialog from '../modules/crm/OfferMailDialog';
import EmailThreadImportDialog, { counterpartOf } from '../modules/crm/EmailThreadImportDialog';
import { DIRECTIONAL_TYPES, DirectionTrail, DirectionToggle } from '../modules/crm/activityDirection';
import AutoFixHighIcon from '@mui/icons-material/AutoFixHigh';
import SlideshowIcon from '@mui/icons-material/Slideshow';
import ForumIcon from '@mui/icons-material/Forum';
import { enrichPerson, mapPersonToContact } from '../services/enrichLayer';
import { sparaKontaktfoto } from '../modules/crm/bildkopia';
import { listPitches } from '../modules/pitch/services/pitchService';
import { bytSkede, sattAterkontakt } from '../modules/crm/pipelineData';
import {
  arsvardeKsek, uppfoljningFranLead, uppfoljningPatch, uppfoljningStatusText,
} from '../modules/crm/pipelineFlode';
import UppfoljningFalt from '../modules/crm/UppfoljningFalt';
import { sorteraAktiviteter, kanDras, flyttaInomDag } from '../modules/crm/aktivitetsordning';
import { StorlekValjare, AterkontaktBekraftelse } from '../modules/crm/PipelineVyer';
import OfferterSektion from '../modules/crm/OfferterSektion';
import KopieraKnapp from '../modules/crm/KopieraKnapp';
import LinkedInHamtning, { kontaktFaltFranLinkedIn } from '../modules/crm/LinkedInHamtning';
import KundprojektKnapp from '../modules/crm/KundprojektKnapp';
import { LeadKundprojekt, KontaktKundprojekt } from '../modules/crm/KundprojektVyer';
import { kopplaRegistreringsaktivitet, laddaKundprojektMedIds } from '../modules/crm/kundprojektData';
import ConstructionIcon from '@mui/icons-material/Construction';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import PostAddIcon from '@mui/icons-material/PostAdd';

const STAGES = [
  { key: 'identified',  label: 'Identifierad',   color: '#78909C', bg: '#ECEFF1' },
  { key: 'qualified',   label: 'Kvalificerad',    color: '#1565C0', bg: '#E3F2FD' },
  { key: 'demo',        label: 'Demo',            color: '#00897B', bg: '#E0F2F1' },
  { key: 'sandbox',     label: 'Sandbox',         color: '#F57C00', bg: '#FFF8E1' },
  { key: 'proposal',    label: 'Offert',          color: '#FF6B00', bg: '#FFF3E0' },
  { key: 'negotiation', label: 'Förhandling',     color: '#6A1B9A', bg: '#F3E5F5' },
  { key: 'pilot',       label: 'Pilot',           color: '#3949AB', bg: '#E8EAF6' },
  { key: 'won',         label: 'Vunnen',          color: '#2E7D32', bg: '#E8F5E9' },
  { key: 'lost',        label: 'Förlorad',        color: '#C62828', bg: '#FFEBEE' },
  { key: 'dormant',     label: 'Vilande',         color: '#795548', bg: '#EFEBE9' },
];

const OFFER_STATUSES = [
  { key: 'skapad',     label: 'Skapad',     color: '#E65100', bg: '#FFF3E0' },
  { key: 'skickad',    label: 'Skickad',    color: '#1565C0', bg: '#E3F2FD' },
  { key: 'uppföljd',   label: 'Uppföljd',   color: '#2E7D32', bg: '#E8F5E9' },
  { key: 'accepterad', label: 'Accepterad', color: '#6A1B9A', bg: '#F3E5F5' },
];

const DEMO_STATUSES = [
  { key: 'bokad',     label: 'Bokad',     color: '#00897B', bg: '#E0F2F1' },
  { key: 'genomförd', label: 'Genomförd', color: '#2E7D32', bg: '#E8F5E9' },
  // Kunden har avbokat och ny tid är inte satt än. Rött, som förfallen
  // uppföljning — det är samma sorts skuld: någon måste höra av sig.
  { key: 'avbokad',   label: 'Avbokad',   color: '#C62828', bg: '#FFEBEE' },
  // Kunden anslöt aldrig. Samma skuld som en avbokning — ny tid måste sättas —
  // men värd att skilja ut: en avbokning är ett besked, ett uteblivet möte är
  // en tyst signal om hur högt affären står på kundens lista.
  { key: 'utebliven', label: 'Kunden kom inte', color: '#AD1457', bg: '#FCE4EC' },
];

const PARTNERSHIP_TAGS = [
  { key: 'IMPENZA', color: '#4527A0', bg: '#EDE7F6' },
];

const BASE_TYPES = [
  { key: 'produkt',     label: 'Produkt' },
  { key: 'konsult',     label: 'Konsulttjänst' },
  { key: 'projekt',     label: 'Fastprisprojekt' },
  { key: 'kombination', label: 'Kombination' },
];

const ACTIVITY_TYPES = [
  { key: 'email',   label: 'E-post',   icon: EmailIcon,       color: '#1565C0' },
  { key: 'phone',   label: 'Samtal',   icon: PhoneIcon,       color: '#2E7D32' },
  { key: 'meeting', label: 'Möte',     icon: GroupsIcon,      color: '#6A1B9A' },
  { key: 'quote',   label: 'Offert',   icon: DescriptionIcon, color: '#E65100' },
  { key: 'note',    label: 'Notering', icon: NoteIcon,        color: '#FF6B00' },
];

const QUICK_LOGS = [
  { type: 'phone',   label: '📞 Ringde',          defaultSummary: 'Ringde – ingen svar', direction: 'out' },
  { type: 'phone',   label: '📞 Samtal',           defaultSummary: 'Telefonsamtal',        direction: 'out' },
  { type: 'phone',   label: '📞 Kunden ringde',    defaultSummary: 'Kunden ringde',        direction: 'in'  },
  { type: 'email',   label: '📧 Mail skickat',     defaultSummary: 'E-post skickad',       direction: 'out' },
  { type: 'email',   label: '📧 Mail mottaget',    defaultSummary: 'E-post från kunden',   direction: 'in'  },
  { type: 'meeting', label: '📅 Möte bokat',       defaultSummary: 'Möte bokat',           direction: 'out' },
  { type: 'quote',   label: '📄 Offert skickad',   defaultSummary: 'Offert skickad',       direction: 'out' },
  // Uteblivet möte: aktiviteten räcker inte, leadet måste också bytas om till
  // en som ropar på ny tid. Därför bär snabbloggen en ändring av själva leadet.
  { type: 'meeting', label: '🚫 Kunden kom inte',  defaultSummary: 'Mötet blev inte av — kunden anslöt inte', direction: 'out',
    danger: true, leadPatch: { demoStatus: 'utebliven' }, followUpToday: true },
];

function activityTypeInfo(key) {
  return ACTIVITY_TYPES.find(t => t.key === key) || ACTIVITY_TYPES[4];
}

function stageInfo(key) {
  return STAGES.find(s => s.key === key) || STAGES[0];
}

function formatSEK(val) {
  if (!val) return '–';
  return Number(val).toLocaleString('sv-SE') + ' KSEK';
}

function formatDate(val) {
  if (!val) return '';
  if (val?.toDate) return val.toDate().toLocaleDateString('sv-SE');
  if (typeof val === 'string') return val;
  return '';
}

/**
 * Senaste överst i aktivitetsloggen.
 *
 * Firestores orderBy('date') jämför bara datumsträngen 'YYYY-MM-DD', så flera
 * aktiviteter samma dag blev oavgjorda och föll tillbaka på dokument-ID-ordning
 * (slumpmässig för auto-ID:n). Inom dagen gäller den ordning man dragit
 * aktiviteterna i (fältet `ordning`), annars createdAt med nyast överst –
 * se modules/crm/aktivitetsordning.js.
 */
const sortActivities = sorteraAktiviteter;

/**
 * En rad i aktivitetsloggen som kan dras i ordning inom samma dag. Handtaget
 * skickas till children(handtag) och visas bara när dagen har fler aktiviteter.
 */
function SorterbarAktivitet({ id, drabar, children }) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id, disabled: !drabar });
  const handtag = drabar ? (
    <Tooltip title="Dra för att ändra ordning inom dagen">
      <Box component="span" {...attributes} {...listeners} aria-label="Dra för att ändra ordning inom dagen"
        sx={{ display: 'inline-flex', cursor: isDragging ? 'grabbing' : 'grab', color: 'text.disabled', touchAction: 'none',
          borderRadius: 0.5, '&:hover': { color: 'text.secondary' }, '&:focus-visible': { outline: '2px solid #FF6B00' } }}>
        <DragIndicatorIcon sx={{ fontSize: 16 }} />
      </Box>
    </Tooltip>
  ) : null;
  return (
    <Box ref={setNodeRef}
      style={{ transform: CSS.Transform.toString(transform), transition }}
      sx={{ position: 'relative', zIndex: isDragging ? 2 : 'auto', bgcolor: isDragging ? 'white' : undefined,
        boxShadow: isDragging ? 3 : 'none', borderRadius: 1 }}>
      {children(handtag)}
    </Box>
  );
}

function isOverdue(dateStr) {
  if (!dateStr) return false;
  return new Date(dateStr) < new Date(new Date().toDateString());
}

function isDueToday(dateStr) {
  if (!dateStr) return false;
  return dateStr === new Date().toISOString().split('T')[0];
}

const PRISMODELL = [
  { key: 'lopande',  label: 'Löpande räkning' },
  { key: 'fastpris', label: 'Fastpris' },
];
const REVENUE_UNITS = [
  { key: 'ar',      label: 'per år' },
  { key: 'projekt', label: 'per projekt' },
  { key: 'manad',   label: 'per månad' },
  { key: 'totalt',  label: 'totalt' },
];
const DEFAULT_DEPLOYMENT_TYPES = [
  { key: 'online',  label: 'Online (SaaS)' },
  { key: 'onprem',  label: 'On-premise' },
  { key: 'oklart',  label: 'Oklart' },
];
const DEFAULT_PLANS = [
  { key: 'enterprise',         label: 'Enterprise' },
  { key: 'projekt',            label: 'Projekt' },
  { key: 'enterprise_projekt', label: 'Enterprise + Projekt' },
];

const SALES_ROLES = [
  {
    key: 'beslutsfattare',
    label: 'Beslutsfattare',
    icon: '♦',
    color: '#1565C0',
    bg: '#E3F2FD',
    desc: 'Äger budgeten och skriver under avtalet. Bryr sig om säkerhet, compliance och strategisk risk — inte produktdetaljerna.',
  },
  {
    key: 'champion',
    label: 'Champion',
    icon: '▲',
    color: '#C62828',
    bg: '#FFEBEE',
    desc: 'Använder verktyget dagligen. Driver frågan uppåt internt. Vill ha ett bättre verktyg och kan argumentera för det.',
  },
  {
    key: 'gatekeeper',
    label: 'Gatekeeper',
    icon: '■',
    color: '#424242',
    bg: '#F5F5F5',
    desc: 'Kan inte säga ja — men kan säga nej. Oroar sig för GDPR, NIS2, LOU-korrekthet, ISO 27001 och systemintegration.',
  },
];

// Uppföljning och påminnelse finns bara på leadet (actUppf), inte på aktiviteten.
const EMPTY_ACTIVITY = { type: 'note', date: '', direction: 'out', contactId: '', contactName: '', summary: '', participants: [], pitchId: '', pitchTitle: '', pitchUrl: '', emailContent: '' };

function parseOutlookParticipants(str) {
  if (!str?.trim()) return [];
  return str.split(';').map(part => {
    part = part.trim();
    if (!part) return null;
    const m = part.match(/^(.+?)\s*<([^>]+)>$/);
    if (m) {
      const name = m[1].trim();
      const email = m[2].trim().toLowerCase();
      return { name: name.includes('@') ? email : name, email };
    }
    return { name: part, email: part.includes('@') ? part.toLowerCase() : '' };
  }).filter(Boolean);
}
// Parse a pasted mail signature / Outlook recipient into contact fields
function parseContactFromPaste(str) {
  if (!str?.trim()) return null;
  const lines = str.split(/\r?\n/).map(l => l.trim()).filter(Boolean);

  // Email regex — eslint-disable-next-line no-useless-escape
  const emailRe = /[\w.+\-]+@[\w\-]+\.[\w.]+/i; // eslint-disable-line no-useless-escape
  // Phone regex (at least 7 digits, may have spaces/dashes/parens)
  const phoneRe = /(\+?[0-9][0-9 ()\\-]{6,}[0-9])/;

  let name = '', email = '', phone = '';

  // Check if first part is Outlook recipient format:  "Name <email>" or "email <email>"
  const singleLine = str.trim();
  const angleMatch = singleLine.match(/^(.+?)\s*<([^>]+)>\s*$/);
  if (angleMatch) {
    const possibleName = angleMatch[1].trim();
    const possibleEmail = angleMatch[2].trim().toLowerCase();
    email = possibleEmail;
    name = possibleName.includes('@') ? '' : possibleName;
    return { name, email, phone };
  }

  // Multi-line signature: scan lines
  for (const line of lines) {
    if (!email) {
      const em = line.match(emailRe);
      if (em) { email = em[0].toLowerCase(); continue; }
    }
    if (!phone) {
      const ph = line.match(phoneRe);
      if (ph) { phone = ph[1].replace(/\s+/g, ' ').trim(); continue; }
    }
    if (!name && !line.includes('@') && !line.match(phoneRe) && line.length > 1 && line.length < 60) {
      name = line;
    }
  }

  return (name || email) ? { name, email, phone } : null;
}

const EMPTY_CONTACT  = { name: '', role: '', department: '', email: '', phone: '', mobile: '', linkedin: '', primary: false, dealRelation: '', salesRoles: [], notes: '', location: '', summary: '', linkedinHeadline: '', currentCompany: '', education: '', languages: '', photoUrl: '', experience: '', certifications: '', skills: '' };

// Plockar ut tio siffror ur ett inklistrat värde. Tar hand om mellanslag,
// tankstreck, punkter och prefix som "Org.nr:", och kortar sekelprefixet 16.
function orgNrDigits(value) {
  let d = String(value || '').replace(/\D/g, '');
  if (d.length === 12 && d.startsWith('16')) d = d.slice(2);
  return /^\d{10}$/.test(d) ? d : '';
}

// Svenska organisationsnummer har en Luhn-kontrollsiffra sist. BolagsAPI avvisar
// nummer som inte stämmer, så det är bättre att fånga det här och säga varför än
// att skicka iväg ett anrop som ändå nekas.
function orgNrChecksumOk(d) {
  let sum = 0;
  for (let i = 0; i < d.length; i++) {
    let n = Number(d[i]);
    if (i % 2 === 0) n *= 2;
    if (n > 9) n -= 9;
    sum += n;
  }
  return sum % 10 === 0;
}

// Returnerar ett giltigt 10-siffrigt org.nr, eller '' om värdet inte är ett.
// Kontrollsiffran ingår i kravet — annars släpps kundnummer som råkar ha tio
// siffror igenom snabbfyllet från bokningsmailet.
function normalizeOrgNr(value) {
  const d = orgNrDigits(value);
  return d && orgNrChecksumOk(d) ? d : '';
}

function parseBookingEmail(str) {
  if (!str?.trim()) return null;
  const lines = str.split(/\r?\n/).map(l => l.trim());
  const emailRe = /[\w.+\-]+@[\w-]+\.[\w.]+/i; // eslint-disable-line no-useless-escape
  const phoneRe = /(\+?[0-9][0-9 ()\-]{6,}[0-9])/; // eslint-disable-line no-useless-escape
  const LABELS = new Set(['Start','Slut','Företag/Person','Kundnummer','Ort',
    'Antal anställda','Bransch','Omsättning','Plats','Ämne','Beskrivning','Resurs']);
  if (!lines.some(l => l === 'Företag/Person' || l === 'Kundnummer' || l === 'Beskrivning')) return null;
  const r = { companyName:'', orgNumber:'', city:'', employees:'', industry:'', revenue:'',
    contactName:'', contactRole:'', contactPhone:'', contactEmail:'',
    meetingStart:'', meetingPlace:'', meetingSubject:'', meetingNotes:'' };
  let i = 0, beskrivIdx = -1, motesIdx = -1;
  while (i < lines.length) {
    const l = lines[i], v = lines[i + 1] || '';
    if (l === 'Start') { r.meetingStart = v.replace(/\s*On\s*$/, '').trim(); i += 2; continue; }
    if (l === 'Företag/Person') { r.companyName = v; i += 2; continue; }
    // Kundnummer från säljsystemet är inte alltid ett organisationsnummer –
    // fyll bara i org.nr-fältet när värdet faktiskt ser ut som ett sådant.
    if (l === 'Kundnummer') { r.orgNumber = normalizeOrgNr(v); i += 2; continue; }
    if (l === 'Ort') { r.city = v; i += 2; continue; }
    if (l === 'Antal anställda') { r.employees = v; i += 2; continue; }
    if (l === 'Bransch') { r.industry = v; i += 2; continue; }
    if (l === 'Omsättning') { r.revenue = v; i += 2; continue; }
    if (l === 'Plats') { r.meetingPlace = v; i += 2; continue; }
    if (l === 'Ämne') { r.meetingSubject = v; i += 2; continue; }
    if (l === 'Beskrivning') { beskrivIdx = i + 1; i++; continue; }
    if (l.startsWith('Möteskommentar')) { motesIdx = i; i++; continue; }
    i++;
  }
  if (beskrivIdx >= 0) {
    const end = motesIdx >= 0 ? motesIdx : lines.length;
    for (const line of lines.slice(beskrivIdx, end).filter(Boolean)) {
      if (LABELS.has(line)) break;
      if (!r.contactEmail) { const m = line.match(emailRe); if (m) { r.contactEmail = m[0].toLowerCase(); continue; } }
      if (!r.contactPhone) { const m = line.match(phoneRe); if (m) { r.contactPhone = m[1].replace(/\s+/g, ' ').trim(); continue; } }
      if (!r.contactName && !line.includes('@') && line.length > 1 && line.length < 60) { r.contactName = line; continue; }
      if (r.contactName && !r.contactRole && !line.includes('@') && line.length > 1 && line.length < 60) { r.contactRole = line; continue; }
    }
  }
  if (motesIdx >= 0) {
    const noteLines = lines.slice(motesIdx + 1);
    const stop = noteLines.findIndex(l => l.startsWith('Bokad tid:') || l === 'Resurs');
    r.meetingNotes = (stop >= 0 ? noteLines.slice(0, stop) : noteLines).filter(Boolean).join('\n').trim();
  }
  return r;
}

export default function CRMLead() {
  const { leadId } = useParams();
  const { currentUser, userProfile } = useAuth();
  const navigate = useNavigate();

  const [lead, setLead] = useState(null);
  const [company, setCompany] = useState(null);
  // Kundprojekt för leadet och kunden (kontaktrutorna och kopplingsdialogen).
  const [kundprojekt, setKundprojekt] = useState([]);
  const [kpLaddar, setKpLaddar] = useState(true);
  const [companyContacts, setCompanyContacts] = useState([]);
  const [offerMailOpen, setOfferMailOpen] = useState(false);
  const [companies, setCompanies] = useState([]);
  const [newCompanyDialog, setNewCompanyDialog] = useState(false);
  const [newCompanyName, setNewCompanyName] = useState('');
  const [departments, setDepartments] = useState([]);
  const [users, setUsers] = useState([]);
  const [products, setProducts] = useState([]);
  const [activities, setActivities] = useState([]);
  const [loading, setLoading] = useState(true);

  const [editing, setEditing] = useState(false);
  const [editForm, setEditForm] = useState({});
  const [saving, setSaving] = useState(false);
  // Bekräftelsen efter flytt till Vilande: { leadId, titel, datum } | null
  const [aterkontakt, setAterkontakt] = useState(null);

  const [contactDialog,    setContactDialog]    = useState(false);
  const [contactForm,      setContactForm]      = useState(EMPTY_CONTACT);
  const [editingContactId, setEditingContactId] = useState(null);
  const [savingContact,    setSavingContact]    = useState(false);

  const [attachments,      setAttachments]      = useState([]);
  const [uploadProgress,   setUploadProgress]   = useState(null); // null | 0-100
  const [uploadStatus,     setUploadStatus]     = useState('');
  const [uploadFailed,     setUploadFailed]     = useState([]);

  const [plingLoading] = useState(false);
  const [celebration, setCelebration] = useState(null); // { type, leadTitle, companyName, value }
  const [plingDialog, setPlingDialog] = useState(null); // 'pling' | 'kling-klang' | null
  const [outlookDialog, setOutlookDialog] = useState(false);
  const [plingPrompt, setPlingPrompt] = useState(null); // 'pling' | 'kling-klang' | null — stage-change prompt
  const [previousPlingText, setPreviousPlingText] = useState(''); // prefill for KLING-KLANG
  const [plingDraftExists, setPlingDraftExists] = useState({ pling: false, 'kling-klang': false });
  const [actDialog, setActDialog] = useState(false);
  const [actForm, setActForm] = useState(EMPTY_ACTIVITY);
  const [actSaving, setActSaving] = useState(false);
  const [threadDialog, setThreadDialog] = useState(false);
  const [editingActivityId, setEditingActivityId] = useState(null);
  const [quickSaving, setQuickSaving] = useState('');
  const [deleteConfirm, setDeleteConfirm] = useState(false);
  const [enrichingContact, setEnrichingContact] = useState(false);
  const [enrichContactMsg, setEnrichContactMsg] = useState('');
  const [contactPaste, setContactPaste] = useState('');
  const [bulkDialog,  setBulkDialog]  = useState(false);
  const [bulkText,    setBulkText]    = useState('');
  const [bulkParsed,  setBulkParsed]  = useState([]);
  const [bulkAdding,  setBulkAdding]  = useState(false);
  const [deploymentTypes, setDeploymentTypes] = useState(DEFAULT_DEPLOYMENT_TYPES);
  const [plans, setPlans] = useState(DEFAULT_PLANS);
  const descRef = React.useRef(null);

  const [colLayout, setColLayout] = useState(() => sessionStorage.getItem('leadColLayout') || '2');
  const [ekonomiOppen, setEkonomiOppen] = useState(false);
  const [ekonomiOppenEdit, setEkonomiOppenEdit] = useState(false);
  const [participantsPaste, setParticipantsPaste] = useState('');
  const [bookingPaste, setBookingPaste] = useState('');
  const [expandedEmailIds, setExpandedEmailIds] = useState(new Set());
  const [pitchesList, setPitchesList] = useState([]);
  const pitchesLoadedRef = React.useRef(false);
  // Kundprojekt registrerade från en ny aktivitet: får aktivitetens id när den sparas.
  const kundprojektVidAktivitetRef = React.useRef([]);
  // Uppföljning-sektionen i aktivitetsdialogen = leadets uppföljningsfält.
  const [actUppf, setActUppf] = useState(() => uppfoljningFranLead());

  const handleColLayout = (n) => { setColLayout(n); sessionStorage.setItem('leadColLayout', n); };

  const load = useCallback(async () => {
    setLoading(true);
    const [leadSnap, companiesSnap, deptsSnap, usersSnap, activitiesSnap, productsSnap] =
      await Promise.all([
        getDoc(doc(db, 'crmLeads', leadId)),
        getDocs(query(collection(db, 'crmCompanies'), orderBy('name'))),
        getDocs(collection(db, 'departments')),
        getDocs(collection(db, 'users')),
        getDocs(query(collection(db, 'crmLeads', leadId, 'activities'), orderBy('date', 'desc'))),
        getDocs(query(collection(db, 'products'), orderBy('name'))),
      ]);

    if (!leadSnap.exists()) { navigate('/crm/leads'); return; }
    const leadData = { id: leadSnap.id, ...leadSnap.data() };
    setLead(leadData);
    setEditForm(leadData);

    const comps = companiesSnap.docs.map(d => ({ id: d.id, ...d.data() }));
    setCompanies(comps);
    setDepartments(deptsSnap.docs.map(d => ({ id: d.id, ...d.data() })));
    setUsers(usersSnap.docs.map(d => ({ id: d.id, ...d.data() })));
    setActivities(sortActivities(activitiesSnap.docs.map(d => ({ id: d.id, ...d.data() }))));
    setProducts(productsSnap.docs.map(d => ({ id: d.id, ...d.data() })));

    const attachSnap = await getDocs(
      query(collection(db, 'crmLeads', leadId, 'attachments'), orderBy('uploadedAt', 'desc'))
    );
    setAttachments(attachSnap.docs.map(d => ({ id: d.id, ...d.data() })));

    // Ladda inställningar för driftsättning och planer
    try {
      const settSnap = await getDoc(doc(db, 'settings', 'options'));
      if (settSnap.exists()) {
        const s = settSnap.data();
        if (s.deploymentTypes?.length) setDeploymentTypes(s.deploymentTypes);
        if (s.plans?.length) setPlans(s.plans);
      }
    } catch (_) {}

    if (leadData.companyId) {
      const comp = comps.find(c => c.id === leadData.companyId) || null;
      setCompany(comp);
      if (comp) {
        const contactsSnap = await getDocs(
          query(collection(db, 'crmCompanies', leadData.companyId, 'contacts'), orderBy('name'))
        );
        setCompanyContacts(contactsSnap.docs.map(d => ({ id: d.id, ...d.data() })));
      }
      setKpLaddar(true);
      laddaKundprojektMedIds([...(leadData.kundprojektIds || []), ...(comp?.kundprojektIds || [])])
        .then(setKundprojekt)
        .catch(e => console.warn('Kunde inte läsa kundprojekt', e))
        .finally(() => setKpLaddar(false));
    } else {
      setKpLaddar(false);
    }
    setLoading(false);
  }, [leadId, navigate]);

  useEffect(() => { load(); }, [load]);

  // Check if PLING/KLING-KLANG drafts exist for this lead
  useEffect(() => {
    if (!leadId) return;
    Promise.all([
      getDoc(doc(db, 'plingDrafts', `${leadId}_pling`)),
      getDoc(doc(db, 'plingDrafts', `${leadId}_kling-klang`)),
    ]).then(([ps, ks]) => {
      setPlingDraftExists({ pling: ps.exists(), 'kling-klang': ks.exists() });
    }).catch(() => {});
  }, [leadId]);

  async function handleSave() {
    setSaving(true);
    // Skedesfälten skrivs bara av bytSkede – formulärets kopia kan vara gammal.
    const { id, skedeSedan, skedeshistorik, ...data } = editForm;
    const fields = {
      ...data,
      estimatedValue: data.estimatedValue ? Number(data.estimatedValue) : null,
      probability: data.probability ? Number(data.probability) : null,
      ...uppfoljningPatch(data, lead, currentUser.uid),
      updatedAt: new Date(),
    };
    if (data.status && data.status !== lead.status) {
      const patch = await bytSkede(leadId, lead, data.status, currentUser.uid, fields);
      if (data.status === 'dormant') visaAterkontakt(patch.nextFollowUp);
    } else {
      await updateDoc(doc(db, 'crmLeads', leadId), fields);
    }
    setSaving(false);
    setEditing(false);
    load();
  }

  async function handleCreateCompany() {
    if (!newCompanyName.trim()) return;
    const docRef = await addDoc(collection(db, 'crmCompanies'), {
      name: newCompanyName.trim(), createdAt: new Date(), status: 'prospect',
    });
    const newComp = { id: docRef.id, name: newCompanyName.trim() };
    setCompanies(prev => [...prev, newComp].sort((a, b) => a.name.localeCompare(b.name)));
    setEditForm(f => ({ ...f, companyId: docRef.id }));
    setNewCompanyName('');
    setNewCompanyDialog(false);
  }

  async function handleSaveActivity() {
    if (!actForm.summary.trim()) return;
    setActSaving(true);

    const dataToSave = {
      ...actForm,
      direction: DIRECTIONAL_TYPES.includes(actForm.type) ? (actForm.direction || 'out') : '',
    };

    if (editingActivityId) {
      const { createdBy, createdAt, ...updatable } = dataToSave;
      await updateDoc(doc(db, 'crmLeads', leadId, 'activities', editingActivityId), updatable);
    } else {
      const aktRef = await addDoc(collection(db, 'crmLeads', leadId, 'activities'), {
        ...dataToSave,
        createdBy: currentUser.uid,
        createdAt: serverTimestamp(),
      });
      const vantande = kundprojektVidAktivitetRef.current;
      kundprojektVidAktivitetRef.current = [];
      await Promise.all(vantande.map(kpId => kopplaRegistreringsaktivitet(kpId, leadId, aktRef.id)
        .catch(e => console.warn('Kunde inte koppla kundprojektet till aktiviteten', e))));
    }
    const leadPatch = uppfoljningPatch(actUppf, lead, currentUser.uid);
    await updateDoc(doc(db, 'crmLeads', leadId), {
      lastActivitySummary: actForm.summary,
      lastActivityDate: actForm.date,
      lastActivityType: actForm.type,
      updatedAt: new Date(),
      ...leadPatch,
      ...(editingActivityId ? {} : { activityCount: increment(1) }),
    });
    setLead(prev => ({ ...prev, ...leadPatch }));
    setActSaving(false);
    setActDialog(false);
    setActForm(EMPTY_ACTIVITY);
    setEditingActivityId(null);
    reloadActivities();
  }

  async function handleQuickLog(quickLog) {
    setQuickSaving(quickLog.label);
    const today = new Date().toISOString().split('T')[0];
    // Uppföljningen flyttas till idag, inte till ett datum vi inte känner —
    // ny tid måste stämmas av med kunden först, och tills dess ska leadet
    // ligga kvar bland dem som kräver något.
    const leadPatch = {
      ...(quickLog.leadPatch || {}),
      ...(quickLog.followUpToday ? { nextFollowUp: today, nextFollowUpTime: '', nextFollowUpStatus: '' } : {}),
    };
    await addDoc(collection(db, 'crmLeads', leadId, 'activities'), {
      type: quickLog.type,
      date: today,
      direction: quickLog.direction,
      summary: quickLog.defaultSummary,
      createdBy: currentUser.uid,
      createdAt: serverTimestamp(),
    });
    await updateDoc(doc(db, 'crmLeads', leadId), {
      lastActivitySummary: quickLog.defaultSummary,
      lastActivityDate: today,
      lastActivityType: quickLog.type,
      updatedAt: new Date(),
      activityCount: increment(1),
      ...leadPatch,
    });
    if (Object.keys(leadPatch).length) setLead(prev => ({ ...prev, ...leadPatch }));
    setQuickSaving('');
    reloadActivities();
  }

  // Loggar varje mail ur en inläst tråd som en egen aktivitet. Datumet i mailet
  // styr var posten hamnar i loggen — inte när importen kördes.
  async function handleImportThread(messages) {
    const today = new Date().toISOString().split('T')[0];
    const sorted = [...messages].sort((a, b) => (a.date || '').localeCompare(b.date || ''));

    for (const msg of sorted) {
      const direction = msg.direction === 'in' ? 'in' : 'out';
      const other = counterpartOf(msg, direction);
      // Koppla mot en känd kontakt när adressen stämmer, så att aktiviteten
      // hänger ihop med kontaktkortet och inte bara bär ett löst namn.
      const match = companyContacts.find(c =>
        (other.email && c.email && c.email.toLowerCase() === other.email.toLowerCase())
        || (other.name && c.name === other.name));
      const summary = [
        msg.subject ? `Ämne: ${msg.subject}` : '',
        msg.summary || '',
      ].filter(Boolean).join('\n');
      const participants = (msg.toNames || []).map((name, i) => ({
        name, email: msg.toEmails?.[i] || '',
      })).filter(pp => pp.name || pp.email);

      // eslint-disable-next-line no-await-in-loop
      await addDoc(collection(db, 'crmLeads', leadId, 'activities'), {
        type: 'email',
        date: msg.date || today,
        direction,
        summary: summary || 'E-post',
        emailContent: msg.body || '',
        contactId: match?.id || '',
        contactName: match?.name || other.name || '',
        participants,
        createdBy: currentUser.uid,
        createdAt: serverTimestamp(),
      });
    }

    // En gammal tråd får inte skriva över leadets senaste aktivitet.
    const last = sorted[sorted.length - 1];
    const lastDate = last?.date || today;
    const isNewest = !lead?.lastActivityDate || lastDate >= lead.lastActivityDate;
    await updateDoc(doc(db, 'crmLeads', leadId), {
      updatedAt: new Date(),
      activityCount: increment(sorted.length),
      ...(isNewest ? {
        lastActivitySummary: last?.summary || last?.subject || 'E-post',
        lastActivityDate: lastDate,
        lastActivityType: 'email',
      } : {}),
    });
    reloadActivities();
  }

  // Loggar en Outlook-bokning som mötesaktivitet på leadet
  async function handleMeetingBooked(meeting, outcome) {
    const kind = meeting.isTeams ? 'Teams-möte' : 'Möte';
    const via = outcome?.via === 'ics'
      ? 'Kalenderfil skapad — öppna i Outlook och skicka'
      : outcome?.sent
        ? `${kind} bokat och inbjudan skickad`
        : `${kind} bokat i kalendern — inbjudan ej skickad`;
    const summary = [
      `${meeting.subject} — kl. ${meeting.time} (${meeting.durationMinutes} min)`,
      meeting.location ? `Plats: ${meeting.location}` : '',
      via,
      outcome?.joinUrl || '',
    ].filter(Boolean).join('\n');

    await addDoc(collection(db, 'crmLeads', leadId, 'activities'), {
      type: 'meeting',
      date: meeting.date,
      direction: 'out',
      summary,
      participants: meeting.attendees.map(a => ({ name: a.name, email: a.email, contactId: a.contactId || '' })),
      contactId: meeting.attendees.find(a => a.contactId)?.contactId || '',
      contactName: meeting.attendees[0]?.name || meeting.contactName || '',
      createdBy: currentUser.uid,
      createdAt: serverTimestamp(),
    });
    // Den som bjuds in till mötet är med i affären.
    await linkContacts(meeting.attendees.map(a => a.contactId).filter(Boolean));
    // Ett bokat möte betyder att demon är bokad, och att nästa avstämning är
    // själva mötet — uppföljningen flyttas dit. Står leadet tidigare i pipen
    // följer steget med, annars syns inte demo-statusen.
    const leadPatch = {
      demoStatus: 'bokad',
      nextFollowUp: meeting.date,
      nextFollowUpTime: meeting.time || '',
      nextFollowUpStatus: 'bokad',
      nastaSteg: meeting.subject ? `Möte: ${meeting.subject}` : 'Möte',
    };
    const fields = {
      lastActivitySummary: summary,
      lastActivityDate: meeting.date,
      lastActivityType: 'meeting',
      updatedAt: new Date(),
      ...leadPatch,
    };
    let skedesPatch = {};
    if (['identified', 'qualified'].includes(lead?.status)) {
      skedesPatch = await bytSkede(leadId, lead, 'demo', currentUser.uid, { ...fields, activityCount: increment(1) });
    } else {
      await updateDoc(doc(db, 'crmLeads', leadId), { ...fields, activityCount: increment(1) });
    }
    const { activityCount: _ac, ...lokalSkedesPatch } = skedesPatch;
    setLead(prev => ({ ...prev, ...leadPatch, ...lokalSkedesPatch }));
    reloadActivities();
  }

  // Loggar offertmailet och flyttar fram offertstatus. Mailet öppnas i Outlook,
  // så vi kan inte veta att det faktiskt skickades — loggen speglar att offerten
  // är på väg, vilket är det man vill se i aktivitetsloggen.
  async function handleOfferMailOpened({ recipients, files, subject, via }) {
    const today = new Date().toISOString().split('T')[0];
    const to = recipients.map(r => `${r.name} <${r.email}>`).join(', ');
    const summary = [
      'Offert skickad',
      subject ? `Ämne: ${subject}` : '',
      to ? `Till: ${to}` : '',
      files.length
        ? `${via === 'eml' ? 'Bifogat' : 'Bifogas manuellt'}: ${files.map(f => f.name).join(', ')}`
        : '',
    ].filter(Boolean).join('\n');

    await addDoc(collection(db, 'crmLeads', leadId, 'activities'), {
      type: 'quote',
      date: today,
      direction: 'out',
      summary,
      participants: recipients.map(r => ({ name: r.name, email: r.email })),
      contactName: recipients[0]?.name || '',
      contactId: recipients[0]?.id || '',
      createdBy: currentUser.uid,
      createdAt: serverTimestamp(),
    });
    await updateDoc(doc(db, 'crmLeads', leadId), {
      offerStatus: 'skickad',
      lastActivitySummary: 'Offert skickad',
      lastActivityDate: today,
      lastActivityType: 'quote',
      updatedAt: new Date(),
      activityCount: increment(1),
    });
    setLead(prev => ({ ...prev, offerStatus: 'skickad' }));
    reloadActivities();
  }

  const dragSensorer = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 4 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }),
  );

  // Aktiviteter samma dag dras i ordning; ordningen sparas i fältet `ordning`.
  async function flyttaAktivitet({ active, over }) {
    const r = flyttaInomDag(activities, active?.id, over?.id);
    if (!r) return;
    setActivities(r.lista);
    try {
      const batch = writeBatch(db);
      for (const u of r.uppdateringar) batch.update(doc(db, 'crmLeads', leadId, 'activities', u.id), { ordning: u.ordning });
      await batch.commit();
    } catch (e) {
      console.error('Kunde inte spara aktiviteternas ordning', e);
      alert('Kunde inte spara ordningen: ' + (e.message || e));
      reloadActivities();
    }
  }

  async function reloadActivities() {
    const snap = await getDocs(query(
      collection(db, 'crmLeads', leadId, 'activities'), orderBy('date', 'desc')
    ));
    setActivities(sortActivities(snap.docs.map(d => ({ id: d.id, ...d.data() }))));
  }

  async function handleDeleteActivity(actId) {
    if (!window.confirm('Ta bort aktiviteten?')) return;
    await deleteDoc(doc(db, 'crmLeads', leadId, 'activities', actId));
    await updateDoc(doc(db, 'crmLeads', leadId), { activityCount: increment(-1) });
    setActivities(prev => prev.filter(a => a.id !== actId));
  }

  async function handleDeleteLead() {
    await deleteDoc(doc(db, 'crmLeads', leadId));
    navigate('/crm/leads');
  }

  async function handlePling(type) {
    if (type === 'kling-klang') await fetchPreviousPling();
    setPlingDialog(type);
  }

  function handlePlingDialogClose(result) {
    const closedType = plingDialog;
    setPlingDialog(null);
    setPlingPrompt(null);
    if (result === 'sent') {
      // Draft was deleted by dialog after send — clear badge
      setPlingDraftExists(prev => ({ ...prev, [closedType]: false }));
      setCelebration({
        type: closedType || 'pling',
        leadTitle: lead.title,
        companyName: company?.name || '',
        value: closedType === 'kling-klang' ? lead.estimatedValue : null,
      });
    } else {
      // Dialog closed without sending — re-check draft existence in case user saved
      if (leadId && closedType) {
        getDoc(doc(db, 'plingDrafts', `${leadId}_${closedType}`))
          .then(snap => setPlingDraftExists(prev => ({ ...prev, [closedType]: snap.exists() })))
          .catch(() => {});
      }
    }
  }

  async function handleStorlek(storlek) {
    await updateDoc(doc(db, 'crmLeads', leadId), { storlek, updatedAt: new Date() });
    setLead(prev => ({ ...prev, storlek }));
    setEditForm(prev => ({ ...prev, storlek }));
  }

  function visaAterkontakt(datum) {
    setAterkontakt({ leadId, titel: lead.title, datum: datum || '' });
  }

  async function andraAterkontakt(datum) {
    const patch = await sattAterkontakt(leadId, datum);
    setLead(prev => ({ ...prev, ...patch }));
    setEditForm(prev => ({ ...prev, ...patch }));
  }

  async function handleStageChange(newStage) {
    if (newStage === lead.status) return;
    const patch = await bytSkede(leadId, lead, newStage, currentUser.uid);
    setLead(prev => ({ ...prev, ...patch }));
    setEditForm(prev => ({ ...prev, ...patch }));
    if (newStage === 'dormant') visaAterkontakt(patch.nextFollowUp || lead.nextFollowUp);
    if (newStage === 'qualified') setPlingPrompt('pling');
    if (newStage === 'won') {
      await fetchPreviousPling();
      setPlingPrompt('kling-klang');
    }
  }

  async function fetchPreviousPling() {
    try {
      const snap = await getDocs(query(collection(db, 'plingKlang'), where('leadId', '==', leadId), where('type', '==', 'pling'), orderBy('createdAt', 'desc'), limit(1)));
      if (!snap.empty) setPreviousPlingText(snap.docs[0].data().aiText || snap.docs[0].data().fallbackText || '');
    } catch { /* index may not exist yet */ }
  }

  // Nytt kundprojekt från leadsidan: håll leadets och kontaktens avledda
  // kundprojektIds aktuella lokalt (Firestore är redan uppdaterat).
  function kundprojektSparat({ id, andringar, kundprojekt: kp }) {
    setKundprojekt(prev => (prev.some(k => k.id === kp.id) ? prev.map(k => (k.id === kp.id ? kp : k)) : [...prev, kp]));
    const med = list => [...new Set([...(list || []), id])];
    const utan = list => (list || []).filter(x => x !== id);
    if (andringar.leads.lagg.includes(leadId)) setLead(prev => ({ ...prev, kundprojektIds: med(prev.kundprojektIds) }));
    if (andringar.leads.ta.includes(leadId)) setLead(prev => ({ ...prev, kundprojektIds: utan(prev.kundprojektIds) }));
    if (andringar.kunder.lagg.includes(lead?.companyId)) setCompany(c => (c ? { ...c, kundprojektIds: med(c.kundprojektIds) } : c));
    const hit = new Set(andringar.kontakter.lagg.map(n => n.split('/')[1]));
    const dit = new Set(andringar.kontakter.ta.map(n => n.split('/')[1]));
    setCompanyContacts(prev => prev.map(c => (hit.has(c.id) ? { ...c, kundprojektIds: med(c.kundprojektIds) }
      : dit.has(c.id) ? { ...c, kundprojektIds: utan(c.kundprojektIds) } : c)));
  }

  // kontakt = förvald kontaktperson (från kontaktkortet), annars huvudkontakten.
  function openActivityDialog(kontakt) {
    kundprojektVidAktivitetRef.current = [];
    const main = kontakt || companyContacts.find(c => c.id === lead?.mainContactId);
    setActUppf(uppfoljningFranLead(lead));
    setActForm({
      ...EMPTY_ACTIVITY,
      date: new Date().toISOString().split('T')[0],
      ...(main ? { contactId: main.id, contactName: main.name || '' } : {}),
    });
    setParticipantsPaste('');
    setBookingPaste('');
    setEditingActivityId(null);
    setActDialog(true);
    if (!pitchesLoadedRef.current && currentUser) {
      listPitches(currentUser.uid, { all: true })
        .then(ps => { setPitchesList(ps); pitchesLoadedRef.current = true; })
        .catch(() => {});
    }
  }

  function openEditActivity(act) {
    const { id, createdBy, createdAt, ...fields } = act;
    setActForm({ ...EMPTY_ACTIVITY, ...fields });
    setActUppf(uppfoljningFranLead(lead));
    setParticipantsPaste('');
    setBookingPaste('');
    setEditingActivityId(id);
    setActDialog(true);
    if (!pitchesLoadedRef.current && currentUser) {
      listPitches(currentUser.uid, { all: true })
        .then(ps => { setPitchesList(ps); pitchesLoadedRef.current = true; })
        .catch(() => {});
    }
  }

  async function handleContactEnrich() {
    if (!contactForm.linkedin) return;
    setEnrichingContact(true);
    setEnrichContactMsg('');
    try {
      const data = await enrichPerson(contactForm.linkedin);
      const mapped = mapPersonToContact(data);
      // Alla mappade fält följer med – tidigare tappades foto, rubrik m.m.
      // här, och syntes först efter ny hämtning och sparning på kundsidan.
      setContactForm(f => ({
        ...f,
        name:             mapped.name             || f.name,
        role:             mapped.role             || f.role,
        email:            mapped.email            || f.email,
        phone:            mapped.phone            || f.phone,
        location:         mapped.location         || f.location,
        summary:          mapped.summary          || f.summary,
        photoUrl:         mapped.photoUrl         || f.photoUrl,
        linkedinHeadline: mapped.linkedinHeadline || f.linkedinHeadline || '',
        currentCompany:   mapped.currentCompany   || f.currentCompany   || '',
        education:        mapped.education        || f.education        || '',
        languages:        mapped.languages        || f.languages        || '',
        experience:       mapped.experience       || f.experience       || '',
        certifications:   mapped.certifications   || f.certifications   || '',
        skills:           mapped.skills           || f.skills           || '',
      }));
      setEnrichContactMsg(`Berikade: ${mapped.name || 'klart'}`);
    } catch (e) {
      setEnrichContactMsg(e.message || 'Kunde inte hämta data');
    } finally {
      setEnrichingContact(false);
    }
  }

  // Kontakter kopplade till leadet. contactIds = alla kopplade, mainContactId =
  // huvudkontakten som blir förval i bokning, offertmail och aktiviteter.
  async function saveLeadContacts(contactIds, mainContactId) {
    const main = contactIds.includes(mainContactId) ? mainContactId : (contactIds[0] || '');
    await updateDoc(doc(db, 'crmLeads', leadId), { contactIds, mainContactId: main, updatedAt: new Date() });
    setLead(prev => ({ ...prev, contactIds, mainContactId: main }));
  }

  function linkContacts(ids) {
    const current = lead?.contactIds || [];
    const added = ids.filter(id => id && !current.includes(id));
    if (!added.length) return Promise.resolve();
    return saveLeadContacts([...current, ...added], lead?.mainContactId || '');
  }

  function unlinkContact(id) {
    const current = lead?.contactIds || [];
    return saveLeadContacts(current.filter(x => x !== id), lead?.mainContactId === id ? '' : lead?.mainContactId || '');
  }

  function setMainContact(id) {
    const current = lead?.contactIds || [];
    return saveLeadContacts(current.includes(id) ? current : [...current, id], id);
  }

  function openNewContact() {
    setContactForm(EMPTY_CONTACT);
    setEditingContactId(null);
    setContactPaste('');
    setEnrichContactMsg('');
    setContactDialog(true);
  }

  function openEditContact(contact) {
    // Normalize legacy dealRelation string → salesRoles array
    const salesRoles = Array.isArray(contact.salesRoles)
      ? contact.salesRoles
      : SALES_ROLES.find(r => r.key === contact.dealRelation)
        ? [contact.dealRelation]
        : [];
    setContactForm({ ...contact, salesRoles });
    setEditingContactId(contact.id);
    setContactPaste('');
    setEnrichContactMsg('');
    setContactDialog(true);
  }

  async function handleSaveContact() {
    if (!lead?.companyId) return;
    setSavingContact(true);
    const { id, ...data } = contactForm;
    if (editingContactId) {
      await updateDoc(doc(db, 'crmCompanies', lead.companyId, 'contacts', editingContactId), data);
      sparaKontaktfoto(lead.companyId, editingContactId, data); // i bakgrunden
    } else {
      // En kontakt som läggs upp från leadet hör till den här affären.
      const ref = await addDoc(collection(db, 'crmCompanies', lead.companyId, 'contacts'), data);
      sparaKontaktfoto(lead.companyId, ref.id, data);
      await linkContacts([ref.id]);
    }
    // Reload contacts
    const snap = await getDocs(query(collection(db, 'crmCompanies', lead.companyId, 'contacts'), orderBy('name')));
    setCompanyContacts(snap.docs.map(d => ({ id: d.id, ...d.data() })));
    setContactDialog(false);
    setSavingContact(false);
  }

  function parseOutlookEmailList(str) {
    if (!str?.trim()) return [];
    const results = [];
    const re = /(?:"([^"]*)"[^<]*<([^>]+)>|<([^>]+)>|([\w.+%-]+@[\w.-]+\.[a-zA-Z]{2,}))/g;
    let m;
    while ((m = re.exec(str)) !== null) {
      const display = (m[1] || '').trim();
      const email = (m[2] || m[3] || m[4] || '').trim().toLowerCase();
      if (!email || results.some(r => r.email === email)) continue;
      let name;
      if (display && !display.includes('@')) {
        name = display;
      } else {
        const local = email.split('@')[0];
        name = local.split(/[.-]/).map(p => p.charAt(0).toUpperCase() + p.slice(1)).join(' ');
      }
      results.push({ name, email });
    }
    return results;
  }

  async function handleBulkAdd() {
    if (!lead?.companyId || !bulkParsed.length) return;
    setBulkAdding(true);
    try {
      for (const { name, email } of bulkParsed) {
        const data = Object.fromEntries(
          Object.entries({ ...EMPTY_CONTACT, name, email }).filter(([, v]) => v !== undefined)
        );
        await addDoc(collection(db, 'crmCompanies', lead.companyId, 'contacts'), data);
      }
      const snap = await getDocs(query(collection(db, 'crmCompanies', lead.companyId, 'contacts'), orderBy('name')));
      setCompanyContacts(snap.docs.map(d => ({ id: d.id, ...d.data() })));
      setBulkDialog(false);
      setBulkText('');
      setBulkParsed([]);
    } finally {
      setBulkAdding(false);
    }
  }

  function uploadOneAttachment(file, index, onProgress) {
    return new Promise((resolve, reject) => {
      const path = `leadAttachments/${leadId}/${Date.now()}_${index}_${file.name}`;
      const fileRef = storageRef(storage, path);
      const task = uploadBytesResumable(fileRef, file);
      task.on('state_changed',
        snap => onProgress(snap.bytesTransferred / snap.totalBytes),
        reject,
        async () => {
          const url = await getDownloadURL(fileRef);
          const docRef = await addDoc(collection(db, 'crmLeads', leadId, 'attachments'), {
            name: file.name, url, storagePath: path,
            size: file.size, mimeType: file.type,
            uploadedBy: currentUser?.uid || '',
            uploadedAt: serverTimestamp(),
          });
          setAttachments(prev => [{
            id: docRef.id, name: file.name, url, storagePath: path,
            size: file.size, mimeType: file.type,
            uploadedAt: { seconds: Date.now() / 1000 },
          }, ...prev]);
          resolve();
        }
      );
    });
  }

  // Filerna laddas upp en i taget så att en trasig fil inte river med sig
  // resten av bunten — de som gick igenom ligger kvar, de som föll bort listas.
  async function handleUploadAttachment(e) {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;
    e.target.value = '';
    setUploadFailed([]);
    setUploadProgress(0);

    const failed = [];
    for (let i = 0; i < files.length; i += 1) {
      const file = files[i];
      setUploadStatus(files.length > 1 ? `${i + 1} av ${files.length} · ${file.name}` : file.name);
      try {
        // eslint-disable-next-line no-await-in-loop
        await uploadOneAttachment(file, i, share => {
          setUploadProgress(Math.round(((i + share) / files.length) * 100));
        });
      } catch {
        failed.push(file.name);
      }
    }

    setUploadProgress(null);
    setUploadStatus('');
    setUploadFailed(failed);
  }

  async function handleDeleteAttachment(att) {
    if (!window.confirm(`Ta bort "${att.name}"?`)) return;
    try { await deleteObject(storageRef(storage, att.storagePath)); } catch (_) {}
    await deleteDoc(doc(db, 'crmLeads', leadId, 'attachments', att.id));
    setAttachments(prev => prev.filter(a => a.id !== att.id));
  }

  // LinkedIn-adress inklistrad på kontaktkortet: hämtat sparas direkt.
  async function sparaKontaktLinkedIn(contact, mapped, url) {
    const falt = kontaktFaltFranLinkedIn(contact, mapped, url);
    await updateDoc(doc(db, 'crmCompanies', lead.companyId, 'contacts', contact.id), falt);
    sparaKontaktfoto(lead.companyId, contact.id, { ...contact, ...falt }); // i bakgrunden
    setCompanyContacts(prev => prev.map(c => (c.id === contact.id ? { ...c, ...falt } : c)));
  }

  async function handleDeleteContact(contactId) {
    if (!lead?.companyId) return;
    if (!window.confirm('Ta bort kontakten?')) return;
    await deleteDoc(doc(db, 'crmCompanies', lead.companyId, 'contacts', contactId));
    if ((lead.contactIds || []).includes(contactId)) await unlinkContact(contactId);
    setCompanyContacts(prev => prev.filter(c => c.id !== contactId));
  }

  if (loading) return (
    <Box sx={{ display: 'flex', justifyContent: 'center', mt: 12 }}>
      <CircularProgress />
    </Box>
  );
  if (!lead) return null;

  const st = stageInfo(lead.status);
  const linkedIdSet = new Set(lead.contactIds || []);
  const linkedContacts = companyContacts.filter(c => linkedIdSet.has(c.id));
  const otherContacts = companyContacts.filter(c => !linkedIdSet.has(c.id));
  const mainContactId = linkedIdSet.has(lead.mainContactId) ? lead.mainContactId : (linkedContacts[0]?.id || '');
  // Förval i dialogerna: leadets huvudkontakt, annars kundens primära som tidigare.
  const defaultContact = companyContacts.find(c => c.id === mainContactId)
    || companyContacts.find(c => c.primary) || companyContacts[0] || null;
  const owner = users.find(u => u.id === lead.ownerId);
  const leadDepts = departments.filter(d => (lead.departmentIds || []).includes(d.id));
  const typeLabel = BASE_TYPES.find(t => t.key === lead.type)?.label || lead.type;
  const product = products.find(p => p.id === lead.productId);
  // Uppföljningen bär mötestiden när ett möte är bokat.
  const followUpWhen = lead.nextFollowUp
    ? `${lead.nextFollowUp}${lead.nextFollowUpTime ? ` kl. ${lead.nextFollowUpTime}` : ''}`
    : '';
  const followUpOverdue = isOverdue(lead.nextFollowUp);
  const followUpToday = isDueToday(lead.nextFollowUp);

  return (
    <Box sx={{ minHeight: '100vh', bgcolor: '#f0f2f5' }}>
      {/* Header */}
      <Box sx={{ bgcolor: '#1a1a1a', color: 'white', py: 1.5, px: 3,
        display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <LagbasLogo />
          <Divider orientation="vertical" flexItem sx={{ borderColor: 'rgba(255,255,255,0.2)', mx: -0.5 }} />
          <IconButton color="inherit" size="small" onClick={() => navigate(-1)}>
            <ArrowBackIcon />
          </IconButton>
          {(company?.logoUrl || company?.name) && (
            <Avatar src={company.logoUrl || undefined}
              sx={{ width: 36, height: 36, bgcolor: '#444', flexShrink: 0, fontSize: '0.75rem' }}>
              {!company.logoUrl && company.name?.split(' ').map(w => w[0]).join('').toUpperCase().slice(0,2)}
            </Avatar>
          )}
          <Box>
            <Typography variant="subtitle1" fontWeight="bold">{lead.title}</Typography>
            <Typography variant="caption" sx={{ color: '#aaa' }}>
              Lead · {typeLabel}{product ? ` – ${product.name}` : ''}{(() => {
                const planLabels = (lead.plan || []).map(k => plans.find(p => p.key === k)?.label).filter(Boolean);
                const drift = deploymentTypes.find(d => d.key === lead.deploymentType)?.label;
                const parts = [...planLabels, drift].filter(Boolean);
                return parts.length ? ` · ${parts.join(' / ')}` : '';
              })()}
            </Typography>
          </Box>
        </Box>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Chip label={st.label} size="small"
            sx={{ bgcolor: st.bg, color: st.color, fontWeight: 700 }} />
          {(lead.hasSandbox || lead.status === 'sandbox') && (
            <Chip label="Sandbox" size="small"
              sx={{ bgcolor: '#FFF8E1', color: '#F57C00', border: '1px solid #FFE082', fontWeight: 600 }} />
          )}
          {lead.offerStatus && (() => {
            const os = OFFER_STATUSES.find(o => o.key === lead.offerStatus);
            return os ? (
              <Chip label={`Offert ${os.label}`} size="small"
                sx={{ bgcolor: os.bg, color: os.color, border: `1px solid ${os.color}55`, fontWeight: 600 }} />
            ) : null;
          })()}
          {lead.demoStatus && (() => {
            const ds = DEMO_STATUSES.find(d => d.key === lead.demoStatus);
            return ds ? (
              <Chip label={`Demo ${ds.label}`} size="small"
                sx={{ bgcolor: ds.bg, color: ds.color, border: `1px solid ${ds.color}55`, fontWeight: 600 }} />
            ) : null;
          })()}
          {(lead.tags || []).map(tag => {
            const pt = PARTNERSHIP_TAGS.find(t => t.key === tag);
            return (
              <Chip key={tag} label={tag} size="small"
                sx={{ bgcolor: pt?.bg || '#EDE7F6', color: pt?.color || '#4527A0',
                  border: `1px solid ${pt?.color || '#4527A0'}55`, fontWeight: 700 }} />
            );
          })}
          <Box sx={{ position: 'relative', display: 'inline-flex' }}>
            <Button size="small" startIcon={<NotificationsActiveIcon />}
              disabled={!!plingLoading} onClick={() => handlePling('pling')}
              sx={{ color: '#FF6B00', borderColor: '#FF6B00', border: '1px solid',
                '&:hover': { bgcolor: 'rgba(255,107,0,0.1)' } }}>
              {plingLoading === 'pling' ? '...' : 'PLING!'}
            </Button>
            {plingDraftExists['pling'] && (
              <Box sx={{ position: 'absolute', top: -4, right: -4, width: 10, height: 10,
                bgcolor: '#FF6B00', borderRadius: '50%', border: '2px solid white' }} />
            )}
          </Box>
          <Box sx={{ position: 'relative', display: 'inline-flex' }}>
            <Button size="small" startIcon={<CelebrationIcon />}
              disabled={!!plingLoading} onClick={() => handlePling('kling-klang')}
              sx={{ color: '#4CAF50', borderColor: '#4CAF50', border: '1px solid',
                '&:hover': { bgcolor: 'rgba(76,175,80,0.1)' } }}>
              {plingLoading === 'kling-klang' ? '...' : 'KLING-KLANG!'}
            </Button>
            {plingDraftExists['kling-klang'] && (
              <Box sx={{ position: 'absolute', top: -4, right: -4, width: 10, height: 10,
                bgcolor: '#4CAF50', borderRadius: '50%', border: '2px solid white' }} />
            )}
          </Box>
          <Button size="small" startIcon={<EditIcon />}
            onClick={() => { setEditForm(lead); setEditing(true); }}
            sx={{ color: 'white', borderColor: 'rgba(255,255,255,0.4)', border: '1px solid' }}>
            Redigera
          </Button>
          <Tooltip title="Radera lead">
            <IconButton size="small" onClick={() => setDeleteConfirm(true)}
              sx={{ color: '#ef5350', border: '1px solid rgba(239,83,80,0.5)',
                '&:hover': { bgcolor: 'rgba(239,83,80,0.15)' } }}>
              <DeleteIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        </Box>
      </Box>

      <Container maxWidth="lg" sx={{ py: 3 }}>
        {/* Follow-up alert */}
        {(followUpOverdue || followUpToday) && !editing && (
          <Alert severity={followUpOverdue ? 'error' : 'warning'}
            icon={<WarningAmberIcon />}
            sx={{ mb: 2, fontWeight: 600 }}
            action={
              <Button size="small" color="inherit"
                onClick={() => {
                  handleSave();
                  const tom = { nextFollowUp: '', nextFollowUpTime: '', nextFollowUpStatus: '', nextFollowUpReminder: false, nextFollowUpReminderUid: '' };
                  updateDoc(doc(db, 'crmLeads', leadId), tom);
                  setLead(p => ({ ...p, ...tom }));
                }}>
                Markera klar
              </Button>
            }>
            {followUpOverdue
              ? `Förfallen uppföljning sedan ${followUpWhen}`
              : `Uppföljning planerad idag (${followUpWhen})`}
          </Alert>
        )}

        {/* Stage pipeline bar */}
        <Paper sx={{ p: 2, mb: 3 }}>
          <Typography variant="caption" color="text.secondary" fontWeight="bold"
            sx={{ textTransform: 'uppercase', letterSpacing: 1, display: 'block', mb: 1.5 }}>
            Pipeline-status
          </Typography>
          <Box sx={{ display: 'flex', gap: 0.8, flexWrap: 'wrap' }}>
            {STAGES.map(s => (
              <Chip key={s.key} label={s.label} size="small"
                onClick={() => handleStageChange(s.key)}
                sx={{
                  bgcolor: lead.status === s.key ? s.color : s.bg,
                  color: lead.status === s.key ? 'white' : s.color,
                  fontWeight: lead.status === s.key ? 700 : 400,
                  cursor: 'pointer', border: `1px solid ${s.color}`,
                  '&:hover': { opacity: 0.85 }
                }} />
            ))}
          </Box>
        </Paper>

        {/* Column layout toggle */}
        <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 2, gap: 0.5 }}>
          <Typography variant="caption" color="text.secondary" sx={{ alignSelf: 'center', mr: 1 }}>
            Kolumner:
          </Typography>
          {['1','2','3'].map(n => (
            <Box key={n} onClick={() => handleColLayout(n)} sx={{
              width: 28, height: 28, display: 'flex', alignItems: 'center', justifyContent: 'center',
              borderRadius: 1, cursor: 'pointer', fontSize: '0.8rem', fontWeight: 700,
              bgcolor: colLayout === n ? '#1a1a1a' : '#f0f0f0',
              color: colLayout === n ? 'white' : 'text.secondary',
              border: '1px solid', borderColor: colLayout === n ? '#1a1a1a' : '#ddd',
              '&:hover': { bgcolor: colLayout === n ? '#333' : '#e0e0e0' },
              userSelect: 'none',
            }}>
              {n}
            </Box>
          ))}
        </Box>

        <Grid container spacing={3}>
          {/* Col 1: Lead info + Contacts */}
          <Grid item xs={colLayout === '1' ? 12 : colLayout === '3' ? 4 : 5}>
            <Paper sx={{ p: 3, mb: 2 }}>
              <Box sx={{ display: 'flex', alignItems: 'center',
                justifyContent: 'space-between', mb: 2.5 }}>
                <Typography variant="subtitle2" fontWeight="bold"
                  sx={{ textTransform: 'uppercase', letterSpacing: 1, color: 'text.secondary' }}>
                  Leadinformation
                </Typography>
                <IconButton size="small" onClick={() => { setEditForm(lead); setEditing(true); }}
                  sx={{ opacity: 0.5, '&:hover': { opacity: 1 } }}>
                  <EditIcon sx={{ fontSize: 16 }} />
                </IconButton>
              </Box>
              {(
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                  {company && (
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <BusinessIcon sx={{ fontSize: 16, color: 'text.disabled' }} />
                      <Typography variant="body2"
                        sx={{ cursor: 'pointer', '&:hover': { textDecoration: 'underline' } }}
                        onClick={() => navigate(`/crm/companies/${company.id}`)}>
                        {company.name}
                      </Typography>
                    </Box>
                  )}
                  {product && (
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <StorefrontIcon sx={{ fontSize: 16, color: '#FF6B00' }} />
                      <Typography variant="body2" fontWeight={600}>{product.name}</Typography>
                      {product.website && (
                        <IconButton size="small" sx={{ p: 0.2 }}
                          onClick={() => window.open(
                            product.website.startsWith('http') ? product.website : 'https://' + product.website,
                            '_blank'
                          )}>
                          <OpenInNewIcon sx={{ fontSize: 14, color: '#1565C0' }} />
                        </IconButton>
                      )}
                    </Box>
                  )}
                  {((lead.plan?.length > 0) || lead.deploymentType) && (
                    <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
                      {(lead.plan || []).map(k => {
                        const label = plans.find(p => p.key === k)?.label;
                        return label ? (
                          <Chip key={k} label={label} size="small"
                            sx={{ fontSize: '0.65rem', height: 20, bgcolor: '#FFF3E0', color: '#FF6B00' }} />
                        ) : null;
                      })}
                      {lead.deploymentType && (() => {
                        const label = deploymentTypes.find(d => d.key === lead.deploymentType)?.label;
                        return label ? (
                          <Chip label={label} size="small"
                            sx={{ fontSize: '0.65rem', height: 20, bgcolor: '#E3F2FD', color: '#1565C0' }} />
                        ) : null;
                      })()}
                    </Box>
                  )}
                  <InfoRow label="Typ" value={typeLabel} />
                  <InfoRow label="Ägare" value={owner ? (owner.displayName || owner.email) : '–'} />
                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1 }}>
                    <Typography variant="caption" color="text.secondary">Storlek</Typography>
                    <StorlekValjare value={lead.storlek || null} onChange={handleStorlek} />
                  </Box>
                  {lead.expectedCloseDate && (
                    <InfoRow label="Förväntat avslut" value={lead.expectedCloseDate} />
                  )}
                  {lead.nextFollowUp && (
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                      <EventIcon sx={{ fontSize: 15,
                        color: followUpOverdue ? '#C62828' : followUpToday ? '#F57C00' : 'text.disabled' }} />
                      <Typography variant="caption"
                        sx={{ color: followUpOverdue ? '#C62828' : followUpToday ? '#F57C00' : 'text.secondary',
                          fontWeight: (followUpOverdue || followUpToday) ? 700 : 400 }}>
                        {lead.nastaSteg ? `Nästa steg: ${lead.nastaSteg} · ${followUpWhen}` : `Uppföljning: ${followUpWhen}`}
                        {uppfoljningStatusText(lead) ? ` · ${uppfoljningStatusText(lead)}` : ''}
                      </Typography>
                    </Box>
                  )}
                  {leadDepts.length > 0 && (
                    <Box>
                      <Typography variant="caption" color="text.secondary">Affärsområden</Typography>
                      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mt: 0.5 }}>
                        {leadDepts.map(d => (
                          <Tooltip key={d.id} title={d.name}>
                            <Chip label={deptCode(d)} size="small"
                              sx={{ fontSize: '0.62rem', height: 18,
                                bgcolor: '#1a1a1a', color: 'white' }} />
                          </Tooltip>
                        ))}
                      </Box>
                    </Box>
                  )}
                  {lead.description && (
                    <Box>
                      <Typography variant="caption" color="text.secondary">Beskrivning</Typography>
                      <Typography variant="body2" sx={{ mt: 0.3 }}>{lead.description}</Typography>
                    </Box>
                  )}
                  {/* Ekonomi (frivilligt) – de gamla värdefälten, hopfällda */}
                  <Box>
                    <Box onClick={() => setEkonomiOppen(o => !o)}
                      sx={{ display: 'flex', alignItems: 'center', gap: 0.5, cursor: 'pointer', userSelect: 'none' }}>
                      <ExpandMoreIcon sx={{ fontSize: 16, color: 'text.secondary',
                        transform: ekonomiOppen ? 'none' : 'rotate(-90deg)', transition: 'transform 0.15s' }} />
                      <Typography variant="caption" color="text.secondary" fontWeight={600}>
                        Ekonomi (frivilligt)
                      </Typography>
                    </Box>
                    <Collapse in={ekonomiOppen}>
                      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.75, pl: 2.5, pt: 0.75 }}>
                        <InfoRow label="Estimerat värde" value={lead.estimatedValue
                          ? `${formatSEK(lead.estimatedValue)} ${REVENUE_UNITS.find(u => u.key === lead.revenueUnit)?.label || ''}`.trim()
                          : '–'} />
                        <InfoRow label="Sannolikhet" value={lead.probability != null && lead.probability !== '' ? `${lead.probability}%` : '–'} />
                        <InfoRow label="Viktat värde" value={(() => {
                          const ar = arsvardeKsek(lead.estimatedValue, lead.revenueUnit);
                          const p = Number(lead.probability);
                          return ar != null && Number.isFinite(p) && p > 0
                            ? `${Math.round(ar * p / 100).toLocaleString('sv-SE')} KSEK per år`
                            : '–';
                        })()} />
                        <InfoRow label="Prismodell" value={PRISMODELL.find(pm => pm.key === lead.prismodell)?.label || '–'} />
                      </Box>
                    </Collapse>
                  </Box>
                  <Divider sx={{ my: 0.5 }} />
                  <InfoRow label="Skapad" value={formatDate(lead.createdAt)} />
                  {lead.updatedAt && <InfoRow label="Uppdaterad" value={formatDate(lead.updatedAt)} />}
                </Box>
              )}
            </Paper>

            <OfferterSektion leadId={leadId} uid={currentUser.uid} />

            <FlatboxOfferterSektion lead={lead} products={products} userProfile={userProfile} />

            {/* Kundprojekt – kundens projekt som säljunderlag (inte Tikabs uppdrag) */}
            {lead?.companyId && (
              <Paper sx={{ p: 2.5, mb: 3 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <ConstructionIcon sx={{ fontSize: 16, color: '#FF6B00' }} />
                  <Typography variant="subtitle2" fontWeight="bold" sx={{ flex: 1 }}>Kundprojekt</Typography>
                  <KundprojektKnapp kund={{ id: lead.companyId, name: company?.name }} leadId={leadId}
                    uid={currentUser?.uid} onSparad={kundprojektSparat} />
                </Box>
                <LeadKundprojekt leadId={leadId} kundId={lead.companyId} laddar={kpLaddar}
                  kps={kundprojekt.filter(kp => (lead.kundprojektIds || []).includes(kp.id))}
                  kundensKps={kundprojekt.filter(kp => (kp.parter || []).some(p => p.kundId === lead.companyId))}
                  uid={currentUser?.uid} onAndrat={kundprojektSparat} navigate={navigate}
                  kontakter={companyContacts} companies={companies} />
              </Paper>
            )}

            {/* Company contacts panel */}
            {lead?.companyId && (
              <Paper sx={{ p: 2.5 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                  <PersonIcon sx={{ fontSize: 16, color: '#FF6B00' }} />
                  <Typography variant="subtitle2" fontWeight="bold" sx={{ flex: 1 }}>
                    Kontakter
                  </Typography>
                  <Button size="small" startIcon={<AddIcon />} onClick={openNewContact}
                    sx={{ color: '#FF6B00', borderColor: '#FF6B00', fontSize: '0.72rem' }}
                    variant="outlined">
                    Lägg till
                  </Button>
                  <Button size="small" variant="outlined"
                    onClick={() => { setBulkText(''); setBulkParsed([]); setBulkDialog(true); }}
                    sx={{ fontSize: '0.72rem', borderColor: '#555', color: '#555',
                      '&:hover': { borderColor: '#222', bgcolor: '#f5f5f5' } }}>
                    Klistra in e-postlista
                  </Button>
                </Box>
                {companyContacts.length === 0 ? (
                  <Typography variant="caption" color="text.secondary" sx={{ fontStyle: 'italic' }}>
                    Inga kontakter registrerade ännu.
                  </Typography>
                ) : (
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                    {linkedContacts.length === 0 && (
                      <Alert severity="info" sx={{ py: 0 }}>
                        <Typography variant="caption">
                          Ingen kontakt är kopplad till leadet. Koppla de personer affären gäller med länkknappen — huvudkontakten (★) blir förval när du bokar möte eller skickar offert.
                        </Typography>
                      </Alert>
                    )}
                    {[...linkedContacts, ...otherContacts].map((contact, idx) => {
                      const linked = idx < linkedContacts.length;
                      const isMain = contact.id === mainContactId;
                      return (
                      <React.Fragment key={contact.id}>
                      {(idx === 0 || idx === linkedContacts.length) && (
                        <Typography variant="caption" fontWeight="bold" color={linked ? '#E65100' : 'text.secondary'}
                          sx={{ textTransform: 'uppercase', letterSpacing: 0.5, fontSize: '0.62rem', mt: idx ? 1 : 0 }}>
                          {linked ? 'Kopplade till leadet' : `Övriga kontakter hos ${company?.name || 'kunden'}`}
                        </Typography>
                      )}
                      <Box sx={{
                        display: 'flex', alignItems: 'flex-start', gap: 1.5,
                        p: 1.5, borderRadius: 1, bgcolor: linked ? '#FFF8F2' : '#f9f9f9',
                        borderLeft: linked ? '3px solid #FF6B00' : '3px solid transparent',
                        opacity: linked ? 1 : 0.85,
                      }}>
                        <Avatar src={contact.photoUrl || undefined}
                          sx={{ bgcolor: '#FF6B00', width: 34, height: 34,
                            fontSize: '0.72rem', flexShrink: 0 }}>
                          {!contact.photoUrl && contact.name?.split(' ').map(w => w[0]).join('').toUpperCase().slice(0,2)}
                        </Avatar>
                        <Box sx={{ flex: 1, minWidth: 0 }}>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                            <Typography variant="body2" fontWeight={600}>{contact.name}</Typography>
                            {isMain && (
                              <Chip label="Huvudkontakt" size="small"
                                sx={{ fontSize: '0.6rem', height: 18, bgcolor: '#FF6B00', color: 'white' }} />
                            )}
                          </Box>
                          {(contact.role || contact.department) && (
                            <Typography variant="caption" color="text.secondary" display="block">
                              {[contact.role, contact.department].filter(Boolean).join(' · ')}
                            </Typography>
                          )}
                          {/* Säljroller-badges */}
                          {(() => {
                            const roles = Array.isArray(contact.salesRoles)
                              ? contact.salesRoles
                              : contact.dealRelation ? [contact.dealRelation] : [];
                            return roles.length > 0 ? (
                              <Box sx={{ display: 'flex', gap: 0.5, mt: 0.5, flexWrap: 'wrap' }}>
                                {roles.map(key => {
                                  const r = SALES_ROLES.find(x => x.key === key);
                                  if (!r) return null;
                                  return (
                                    <Tooltip key={key} title={r.desc} placement="top" arrow>
                                      <Chip
                                        size="small"
                                        label={`${r.icon} ${r.label}`}
                                        sx={{
                                          height: 18, fontSize: '0.62rem', fontWeight: 700,
                                          bgcolor: r.bg, color: r.color,
                                          border: `1px solid ${r.color}30`,
                                          cursor: 'help',
                                        }}
                                      />
                                    </Tooltip>
                                  );
                                })}
                              </Box>
                            ) : null;
                          })()}
                          <Box sx={{ display: 'flex', gap: 1, mt: 0.5, flexWrap: 'wrap' }}>
                            {contact.mobile && (
                              <Box component="a" href={`tel:${contact.mobile}`}
                                sx={{ display: 'flex', alignItems: 'center', gap: 0.3,
                                  color: '#2E7D32', textDecoration: 'none',
                                  '&:hover': { textDecoration: 'underline' } }}>
                                <SmartphoneIcon sx={{ fontSize: 11 }} />
                                <Typography variant="caption">{contact.mobile}</Typography>
                              </Box>
                            )}
                            {contact.mobile && <KopieraKnapp text={contact.mobile} size={11} sx={{ ml: -0.75 }} />}
                            {contact.phone && (
                              <Box component="a" href={`tel:${contact.phone}`}
                                sx={{ display: 'flex', alignItems: 'center', gap: 0.3,
                                  color: '#2E7D32', textDecoration: 'none',
                                  '&:hover': { textDecoration: 'underline' } }}>
                                <PhoneIcon sx={{ fontSize: 11 }} />
                                <Typography variant="caption">{contact.phone}</Typography>
                              </Box>
                            )}
                            {contact.phone && <KopieraKnapp text={contact.phone} size={11} sx={{ ml: -0.75 }} />}
                            {contact.email && (
                              <Box component="a" href={`mailto:${contact.email}`}
                                sx={{ display: 'flex', alignItems: 'center', gap: 0.3,
                                  color: '#1565C0', textDecoration: 'none',
                                  '&:hover': { textDecoration: 'underline' } }}>
                                <EmailIcon sx={{ fontSize: 11 }} />
                                <Typography variant="caption" noWrap>{contact.email}</Typography>
                              </Box>
                            )}
                            {contact.email && <KopieraKnapp text={contact.email} size={11} sx={{ ml: -0.75 }} />}
                          </Box>
                          {!contact.linkedin && linked && (
                            <LinkedInHamtning typ="person" kompakt namn={contact.name} foretag={company?.name}
                              onHamtad={(mapped, url) => sparaKontaktLinkedIn(contact, mapped, url)} />
                          )}
                          <KontaktKundprojekt kps={kundprojekt} kundId={lead.companyId} kontaktId={contact.id} navigate={navigate} />
                          <KundprojektKnapp liten sx={{ mt: 0.25, ml: -0.75 }}
                            kund={{ id: lead.companyId, name: company?.name }}
                            kontakt={{ id: contact.id, name: contact.name }} leadId={leadId}
                            uid={currentUser?.uid} onSparad={kundprojektSparat} />
                        </Box>
                        <Box sx={{ display: 'flex', gap: 0.25, flexShrink: 0 }}>
                          {linked && (
                            <Tooltip title={isMain ? 'Huvudkontakt för leadet' : 'Gör till huvudkontakt'}>
                              <span>
                                <IconButton size="small" disabled={isMain} onClick={() => setMainContact(contact.id)}
                                  sx={{ color: '#FF6B00' }}>
                                  {isMain ? <StarIcon sx={{ fontSize: 15 }} /> : <StarBorderIcon sx={{ fontSize: 15 }} />}
                                </IconButton>
                              </span>
                            </Tooltip>
                          )}
                          <Tooltip title={linked ? 'Koppla bort från leadet' : 'Koppla till leadet'}>
                            <IconButton size="small"
                              onClick={() => (linked ? unlinkContact(contact.id) : linkContacts([contact.id]))}
                              sx={{ color: linked ? 'text.secondary' : '#FF6B00' }}>
                              {linked ? <LinkOffIcon sx={{ fontSize: 15 }} /> : <LinkIcon sx={{ fontSize: 15 }} />}
                            </IconButton>
                          </Tooltip>
                          <Tooltip title={`Logga aktivitet med ${contact.name || 'kontakten'}`}>
                            <IconButton size="small" onClick={() => openActivityDialog(contact)}
                              sx={{ color: '#FF6B00' }}>
                              <PostAddIcon sx={{ fontSize: 15 }} />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="Redigera">
                            <IconButton size="small" onClick={() => openEditContact(contact)}>
                              <EditIcon sx={{ fontSize: 14 }} />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="Ta bort">
                            <IconButton size="small" onClick={() => handleDeleteContact(contact.id)}
                              sx={{ color: '#C62828' }}>
                              <DeleteIcon sx={{ fontSize: 14 }} />
                            </IconButton>
                          </Tooltip>
                        </Box>
                      </Box>
                      </React.Fragment>
                      );
                    })}
                  </Box>
                )}
              </Paper>
            )}
          </Grid>

          {/* Col 2: Activity log (top) + Attachments (bottom) */}
          <Grid item xs={colLayout === '1' ? 12 : colLayout === '3' ? 4 : 7}>

            {/* ── Aktivitetslogg ── */}
            <Paper sx={{ p: 3, mb: colLayout !== '3' ? 3 : 0 }}>
              <Box sx={{ display: 'flex', alignItems: 'center',
                justifyContent: 'space-between', mb: 2 }}>
                <Typography variant="subtitle2" fontWeight="bold"
                  sx={{ textTransform: 'uppercase', letterSpacing: 1, color: 'text.secondary' }}>
                  Aktivitetslogg
                </Typography>
                <Box sx={{ display: 'flex', gap: 1 }}>
                  <Button size="small" variant="outlined" startIcon={<EventIcon />}
                    onClick={() => setOutlookDialog(true)}
                    sx={{ borderColor: '#1565C0', color: '#1565C0' }}>
                    Boka möte
                  </Button>
                  <Button size="small" variant="outlined" startIcon={<ForumIcon />}
                    onClick={() => setThreadDialog(true)}
                    sx={{ borderColor: '#1565C0', color: '#1565C0' }}>
                    Mailtråd
                  </Button>
                  <Button size="small" variant="outlined" startIcon={<DescriptionIcon />}
                    onClick={() => setOfferMailOpen(true)}
                    sx={{ borderColor: '#6A1B9A', color: '#6A1B9A' }}>
                    Offertmail
                  </Button>
                  <Button size="small" variant="outlined" startIcon={<AddIcon />}
                    onClick={() => openActivityDialog()}
                    sx={{ borderColor: '#FF6B00', color: '#FF6B00' }}>
                    Lägg till
                  </Button>
                </Box>
              </Box>

              {/* Quick log buttons */}
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.75, mb: 2.5,
                p: 1.5, bgcolor: '#f8f9fa', borderRadius: 2 }}>
                <Typography variant="caption" color="text.secondary" fontWeight="bold"
                  sx={{ width: '100%', mb: 0.5, textTransform: 'uppercase', letterSpacing: 0.5 }}>
                  Snabblogg
                </Typography>
                {QUICK_LOGS.map((ql, i) => (
                  <Chip key={i}
                    label={quickSaving === ql.label ? 'Sparar...' : ql.label}
                    size="small"
                    onClick={() => handleQuickLog(ql)}
                    disabled={!!quickSaving}
                    sx={{
                      cursor: 'pointer', fontWeight: 500,
                      bgcolor: 'white', border: '1px solid',
                      borderColor: ql.danger ? '#F8BBD0' : 'divider',
                      color: ql.danger ? '#AD1457' : undefined,
                      fontSize: '0.72rem',
                      '&:hover': ql.danger
                        ? { bgcolor: '#FCE4EC', borderColor: '#AD1457' }
                        : { bgcolor: '#fff3e0', borderColor: '#FF6B00' },
                    }} />
                ))}
              </Box>

              {activities.length === 0 ? (
                <Box sx={{ py: 4, textAlign: 'center' }}>
                  <NoteIcon sx={{ fontSize: 40, color: 'text.disabled', mb: 1 }} />
                  <Typography variant="body2" color="text.secondary">
                    Inga aktiviteter registrerade än.
                  </Typography>
                </Box>
              ) : (
                <DndContext sensors={dragSensorer} collisionDetection={closestCenter} onDragEnd={flyttaAktivitet}>
                <SortableContext items={activities.map(a => a.id)} strategy={verticalListSortingStrategy}>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0 }}>
                  {activities.map((act, idx) => {
                    const aType = activityTypeInfo(act.type);
                    const Icon = aType.icon;
                    const isLast = idx === activities.length - 1;
                    return (
                      <SorterbarAktivitet key={act.id} id={act.id} drabar={kanDras(activities, act)}>
                      {handtag => (
                      <Box sx={{ display: 'flex', gap: 2 }}>
                        <Box sx={{ display: 'flex', flexDirection: 'column',
                          alignItems: 'center', width: 32, flexShrink: 0 }}>
                          <Avatar sx={{ width: 28, height: 28,
                            bgcolor: aType.color + '22', mt: 0.5 }}>
                            <Icon sx={{ fontSize: 14, color: aType.color }} />
                          </Avatar>
                          {!isLast && (
                            <Box sx={{ width: 2, flex: 1, bgcolor: 'divider',
                              mt: 0.5, mb: 0.5, minHeight: 20 }} />
                          )}
                        </Box>
                        <Box sx={{ flex: 1, pb: isLast ? 0 : 2 }}>
                          <Box sx={{ display: 'flex', alignItems: 'flex-start',
                            justifyContent: 'space-between', gap: 1 }}>
                            <Box sx={{ flex: 1 }}>
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                                {handtag}
                                <Chip label={aType.label} size="small"
                                  sx={{ fontSize: '0.6rem', height: 18,
                                    bgcolor: aType.color + '22', color: aType.color }} />
                                {act.direction && DIRECTIONAL_TYPES.includes(act.type) ? (
                                  <DirectionTrail dense
                                    direction={act.direction}
                                    counterpartLabel={act.contactName || company?.name} />
                                ) : act.contactName ? (
                                  <Typography variant="caption" color="text.secondary">
                                    {act.contactName}
                                  </Typography>
                                ) : null}
                                {act.date && (
                                  <Typography variant="caption" color="text.disabled">
                                    {act.date}
                                  </Typography>
                                )}
                                {act.kallelseStatus && (
                                  <Chip size="small"
                                    label={act.kallelseStatus === 'öppnad'
                                      ? `Kallelse öppnad${act.kallelseVia === 'ics' ? ' (ICS)' : ''}`
                                      : 'Kallelse ej öppnad'}
                                    sx={{ height: 18, fontSize: '0.62rem', fontWeight: 600,
                                      bgcolor: act.kallelseStatus === 'öppnad' ? '#E3F2FD' : '#FFF3E0',
                                      color: act.kallelseStatus === 'öppnad' ? '#1565C0' : '#E65100' }} />
                                )}
                              </Box>
                              <Typography variant="body2" sx={{ mt: 0.5 }}>{act.summary}</Typography>
                              {act.pitchId && act.pitchUrl && (
                                <Chip
                                  icon={<SlideshowIcon sx={{ fontSize: '12px !important' }} />}
                                  label={act.pitchTitle || 'Presentation'}
                                  size="small"
                                  clickable
                                  onClick={() => window.open(act.pitchUrl, '_blank')}
                                  sx={{ mt: 0.75, fontSize: '0.65rem', height: 20,
                                    bgcolor: '#FFF3E0', color: '#FF6B00',
                                    '& .MuiChip-icon': { color: '#FF6B00' } }}
                                />
                              )}
                              {act.participants?.length > 0 && (
                                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mt: 0.75 }}>
                                  {act.participants.map((p, pi) => (
                                    <Chip key={pi} label={p.name || p.email} size="small"
                                      title={p.email}
                                      sx={{ fontSize: '0.6rem', height: 18, bgcolor: '#e8f4fd', color: '#1565C0' }} />
                                  ))}
                                </Box>
                              )}
                              {act.emailContent && (
                                <Box sx={{ mt: 0.75 }}>
                                  <Chip
                                    icon={<EmailIcon sx={{ fontSize: '11px !important' }} />}
                                    label={expandedEmailIds.has(act.id) ? 'Dölj mail' : 'Visa mail'}
                                    size="small"
                                    clickable
                                    onClick={() => setExpandedEmailIds(prev => {
                                      const next = new Set(prev);
                                      next.has(act.id) ? next.delete(act.id) : next.add(act.id);
                                      return next;
                                    })}
                                    sx={{ fontSize: '0.6rem', height: 18, bgcolor: '#E3F2FD', color: '#1565C0',
                                      '& .MuiChip-icon': { color: '#1565C0' } }}
                                  />
                                  {expandedEmailIds.has(act.id) && (
                                    <Box sx={{
                                      mt: 0.75, p: 1.5, bgcolor: '#f8fbff',
                                      border: '1px solid #BBDEFB', borderRadius: 1,
                                      maxHeight: 220, overflowY: 'auto',
                                    }}>
                                      <Typography variant="caption"
                                        sx={{ whiteSpace: 'pre-wrap', color: 'text.secondary',
                                          fontSize: '0.76rem', lineHeight: 1.55 }}>
                                        {act.emailContent}
                                      </Typography>
                                    </Box>
                                  )}
                                </Box>
                              )}
                            </Box>
                            <Box sx={{ display: 'flex', flexShrink: 0 }}>
                              <IconButton size="small" onClick={() => openEditActivity(act)}
                                sx={{ opacity: 0.3, '&:hover': { opacity: 1, color: 'primary.main' } }}>
                                <EditIcon sx={{ fontSize: 14 }} />
                              </IconButton>
                              <IconButton size="small" onClick={() => handleDeleteActivity(act.id)}
                                sx={{ opacity: 0.3, '&:hover': { opacity: 1, color: 'error.main' } }}>
                                <DeleteIcon sx={{ fontSize: 14 }} />
                              </IconButton>
                            </Box>
                          </Box>
                        </Box>
                      </Box>
                      )}
                      </SorterbarAktivitet>
                    );
                  })}
                </Box>
                </SortableContext>
                </DndContext>
              )}
            </Paper>

            {/* Attachments (in col 2, unless 3-col layout where it's col 3) */}
            {colLayout !== '3' && (
              <Paper sx={{ p: 2.5 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                  <AttachFileIcon sx={{ fontSize: 16, color: '#FF6B00' }} />
                  <Typography variant="subtitle2" fontWeight="bold" sx={{ flex: 1 }}>
                    Bifogade handlingar
                  </Typography>
                  <Button component="label" size="small" variant="outlined"
                    startIcon={<AddIcon />}
                    sx={{ fontSize: '0.72rem', color: '#FF6B00', borderColor: '#FF6B00' }}>
                    Bifoga
                    <input type="file" hidden multiple onChange={handleUploadAttachment}
                      accept=".pdf,.doc,.docx,.xls,.xlsx,.ppt,.pptx,.txt,.png,.jpg,.jpeg,.gif,.zip" />
                  </Button>
                </Box>
                {uploadProgress !== null && (
                  <Box sx={{ mb: 2 }}>
                    <Typography variant="caption" color="text.secondary" sx={{ mb: 0.5, display: 'block' }}>
                      Laddar upp {uploadStatus} — {uploadProgress}%
                    </Typography>
                    <LinearProgress variant="determinate" value={uploadProgress}
                      sx={{ borderRadius: 1, '& .MuiLinearProgress-bar': { bgcolor: '#FF6B00' } }} />
                  </Box>
                )}
                {uploadFailed.length > 0 && (
                  <Alert severity="warning" sx={{ mb: 2 }} onClose={() => setUploadFailed([])}>
                    Kunde inte ladda upp: {uploadFailed.join(', ')}
                  </Alert>
                )}
                {attachments.length === 0 && uploadProgress === null ? (
                  <Typography variant="caption" color="text.secondary" sx={{ fontStyle: 'italic' }}>
                    Inga handlingar bifogade ännu.
                  </Typography>
                ) : (
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                    {attachments.map(att => (
                      <Box key={att.id} sx={{
                        display: 'flex', alignItems: 'center', gap: 1.5,
                        p: 1.25, borderRadius: 1, bgcolor: '#f9f9f9',
                        border: '1px solid', borderColor: 'divider',
                      }}>
                        <InsertDriveFileIcon sx={{ fontSize: 22, color: fileColor(att.mimeType), flexShrink: 0 }} />
                        <Box sx={{ flex: 1, minWidth: 0 }}>
                          <Typography variant="body2" fontWeight={500} noWrap title={att.name}>
                            {att.name}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            {formatBytes(att.size)}
                          </Typography>
                        </Box>
                        <Tooltip title="Öppna / ladda ner">
                          <IconButton size="small" component="a" href={att.url} target="_blank" rel="noopener noreferrer">
                            <DownloadIcon sx={{ fontSize: 16 }} />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Ta bort">
                          <IconButton size="small" onClick={() => handleDeleteAttachment(att)}
                            sx={{ color: '#C62828' }}>
                            <DeleteIcon sx={{ fontSize: 16 }} />
                          </IconButton>
                        </Tooltip>
                      </Box>
                    ))}
                  </Box>
                )}
              </Paper>
            )}
          </Grid>

          {/* Col 3 (only in 3-col mode): Attachments */}
          {colLayout === '3' && (
            <Grid item xs={4}>
              <Paper sx={{ p: 2.5 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                  <AttachFileIcon sx={{ fontSize: 16, color: '#FF6B00' }} />
                  <Typography variant="subtitle2" fontWeight="bold" sx={{ flex: 1 }}>
                    Bifogade handlingar
                  </Typography>
                  <Button component="label" size="small" variant="outlined"
                    startIcon={<AddIcon />}
                    sx={{ fontSize: '0.72rem', color: '#FF6B00', borderColor: '#FF6B00' }}>
                    Bifoga
                    <input type="file" hidden multiple onChange={handleUploadAttachment}
                      accept=".pdf,.doc,.docx,.xls,.xlsx,.ppt,.pptx,.txt,.png,.jpg,.jpeg,.gif,.zip" />
                  </Button>
                </Box>
                {uploadProgress !== null && (
                  <Box sx={{ mb: 2 }}>
                    <Typography variant="caption" color="text.secondary" sx={{ mb: 0.5, display: 'block' }}>
                      Laddar upp {uploadStatus} — {uploadProgress}%
                    </Typography>
                    <LinearProgress variant="determinate" value={uploadProgress}
                      sx={{ borderRadius: 1, '& .MuiLinearProgress-bar': { bgcolor: '#FF6B00' } }} />
                  </Box>
                )}
                {uploadFailed.length > 0 && (
                  <Alert severity="warning" sx={{ mb: 2 }} onClose={() => setUploadFailed([])}>
                    Kunde inte ladda upp: {uploadFailed.join(', ')}
                  </Alert>
                )}
                {attachments.length === 0 && uploadProgress === null ? (
                  <Typography variant="caption" color="text.secondary" sx={{ fontStyle: 'italic' }}>
                    Inga handlingar bifogade ännu.
                  </Typography>
                ) : (
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                    {attachments.map(att => (
                      <Box key={att.id} sx={{
                        display: 'flex', alignItems: 'center', gap: 1.5,
                        p: 1.25, borderRadius: 1, bgcolor: '#f9f9f9',
                        border: '1px solid', borderColor: 'divider',
                      }}>
                        <InsertDriveFileIcon sx={{ fontSize: 22, color: fileColor(att.mimeType), flexShrink: 0 }} />
                        <Box sx={{ flex: 1, minWidth: 0 }}>
                          <Typography variant="body2" fontWeight={500} noWrap title={att.name}>
                            {att.name}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            {formatBytes(att.size)}
                          </Typography>
                        </Box>
                        <Tooltip title="Öppna / ladda ner">
                          <IconButton size="small" component="a" href={att.url} target="_blank" rel="noopener noreferrer">
                            <DownloadIcon sx={{ fontSize: 16 }} />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Ta bort">
                          <IconButton size="small" onClick={() => handleDeleteAttachment(att)}
                            sx={{ color: '#C62828' }}>
                            <DeleteIcon sx={{ fontSize: 16 }} />
                          </IconButton>
                        </Tooltip>
                      </Box>
                    ))}
                  </Box>
                )}
              </Paper>
            </Grid>
          )}
        </Grid>
      </Container>

      {/* Add/edit activity dialog */}
      <Dialog open={actDialog} onClose={() => { setActDialog(false); setEditingActivityId(null); }} maxWidth="md" fullWidth
        PaperProps={{ sx: { borderRadius: 3 } }}>
        <DialogTitle sx={{ pb: 1 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
            <NoteIcon sx={{ color: '#FF6B00', fontSize: 28 }} />
            <Box>
              <Typography variant="h6" fontWeight="bold">{editingActivityId ? 'Redigera aktivitet' : 'Ny aktivitet'}</Typography>
              <Typography variant="caption" color="text.secondary">
                {editingActivityId ? 'Uppdatera aktivitetens innehåll' : 'Logga vad som hänt i affären'}
              </Typography>
            </Box>
          </Box>
        </DialogTitle>
        <Divider />
        <DialogContent sx={{ pt: 3 }}>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2.5 }}>

            {/* ── Snabbfyll från bokningsmail ── */}
            {!editingActivityId && (
              <Box sx={{ bgcolor: '#fff8e1', borderRadius: 2, p: 2, border: '1px solid #ffe082' }}>
                <Typography variant="caption" fontWeight="bold"
                  sx={{ color: '#E65100', display: 'block', mb: 1, textTransform: 'uppercase', letterSpacing: 0.5 }}>
                  Snabbfyll — klistra in bokningsmail
                </Typography>
                <TextField fullWidth size="small" multiline minRows={1}
                  placeholder="Klistra in hela bokningsmailet från säljsystemet här"
                  value={bookingPaste}
                  onChange={e => {
                    const val = e.target.value;
                    setBookingPaste(val);
                    const booking = parseBookingEmail(val);
                    if (booking) {
                      const [dateStr, timeStr] = booking.meetingStart
                        ? [booking.meetingStart.split(' ')[0], booking.meetingStart.split(' ')[1] || '']
                        : ['', ''];
                      const prefix = [
                        timeStr && `Kl. ${timeStr}`,
                        booking.meetingPlace && `(${booking.meetingPlace})`,
                      ].filter(Boolean).join(' ');
                      const summary = [prefix, booking.meetingNotes].filter(Boolean).join('\n\n');
                      const participants = booking.contactName
                        ? [{ name: booking.contactName, email: booking.contactEmail || '' }]
                        : [];
                      setActForm(f => ({
                        ...f,
                        type: 'meeting',
                        ...(dateStr ? { date: dateStr } : {}),
                        ...(summary && !f.summary ? { summary } : {}),
                        ...(booking.contactName && !f.contactName ? { contactName: booking.contactName } : {}),
                        ...(participants.length > 0 && (!f.participants || f.participants.length === 0) ? { participants } : {}),
                      }));
                    }
                  }}
                  helperText="Typ, datum, kontaktperson, deltagare och mötesanteckningar fylls i automatiskt"
                  InputProps={{ sx: { bgcolor: 'white' } }}
                />
              </Box>
            )}

            {/* Rad 1: Typ + Datum sida vid sida */}
            <Box sx={{ display: 'flex', gap: 2 }}>
              <TextField select label="Typ" sx={{ flex: 1 }}
                value={actForm.type}
                onChange={e => setActForm(f => ({ ...f, type: e.target.value }))}>
                {ACTIVITY_TYPES.map(t => (
                  <MenuItem key={t.key} value={t.key}>{t.label}</MenuItem>
                ))}
              </TextField>
              <TextField label="Datum" type="date" sx={{ flex: 1 }}
                InputLabelProps={{ shrink: true }}
                value={actForm.date}
                onChange={e => setActForm(f => ({ ...f, date: e.target.value }))} />
            </Box>

            {/* Rad 2: Kontaktperson – full bredd med (+) */}
            <Box sx={{ display: 'flex', gap: 1, alignItems: 'flex-start' }}>
              {companyContacts.length > 0 ? (
                <TextField select fullWidth label="Kontaktperson (hos kunden)"
                  value={actForm.contactId || ''}
                  onChange={e => {
                    const c = companyContacts.find(x => x.id === e.target.value);
                    setActForm(f => ({ ...f, contactId: e.target.value, contactName: c?.name || '' }));
                  }}>
                  <MenuItem value="">– Välj kontakt –</MenuItem>
                  {companyContacts.map(c => (
                    <MenuItem key={c.id} value={c.id}>{c.name}{c.role ? ` (${c.role})` : ''}</MenuItem>
                  ))}
                </TextField>
              ) : (
                <TextField fullWidth label="Kontaktperson (hos kunden)"
                  placeholder="Namn på personen du pratat med"
                  value={actForm.contactName || ''}
                  onChange={e => setActForm(f => ({ ...f, contactName: e.target.value }))} />
              )}
              <Tooltip title="Lägg till ny kontakt">
                <IconButton onClick={openNewContact} size="medium"
                  sx={{ mt: 0.5, border: '1px solid rgba(0,0,0,0.23)', borderRadius: 1, flexShrink: 0 }}>
                  <AddIcon />
                </IconButton>
              </Tooltip>
            </Box>

            {/* Riktning – bara för typer där någon faktiskt kontaktat någon */}
            {DIRECTIONAL_TYPES.includes(actForm.type) && (
              <DirectionToggle
                direction={actForm.direction}
                counterpartLabel={actForm.contactName || company?.name}
                onChange={dir => setActForm(f => ({ ...f, direction: dir }))} />
            )}

            {/* Rad 3: Sammanfattning – full bredd, stöder radbrytningar */}
            <TextField fullWidth label="Sammanfattning *"
              multiline rows={6}
              value={actForm.summary}
              onChange={e => setActForm(f => ({ ...f, summary: e.target.value }))}
              placeholder="Vad diskuterades? Vad beslutades? Nästa steg? Stämning och signaler?"
              helperText="Stöder radbrytningar — använd Enter för ny rad, - för punktlista" />

            {/* Skapa offert — visas bara för Offert */}
            {actForm.type === 'quote' && (
              <Box sx={{
                p: 2, border: '1px solid', borderColor: '#E65100',
                borderRadius: 2, bgcolor: '#FFF8F5',
                display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 2,
              }}>
                <Box>
                  <Typography variant="body2" fontWeight={600} color="#E65100">Skapa offert i offertmodulen</Typography>
                  <Typography variant="caption" color="text.secondary">
                    Öppnar Ny offert med kundens uppgifter förifyllda
                  </Typography>
                </Box>
                <Button variant="contained" size="small" startIcon={<DescriptionIcon />}
                  onClick={() => { setActDialog(false); navigate(`/offers?new=1`); }}
                  sx={{ bgcolor: '#E65100', '&:hover': { bgcolor: '#bf360c' }, whiteSpace: 'nowrap', flexShrink: 0 }}>
                  Skapa offert
                </Button>
              </Box>
            )}

            {/* Inklistrat mailinnehåll — visas bara för E-post */}
            {actForm.type === 'email' && (
              <Box sx={{
                border: '1px solid',
                borderColor: actForm.emailContent ? '#1565C0' : 'divider',
                borderRadius: 2, overflow: 'hidden',
              }}>
                <Box sx={{
                  px: 2, py: 1.25,
                  bgcolor: actForm.emailContent ? '#E3F2FD' : '#f5f5f5',
                  display: 'flex', alignItems: 'center', gap: 1,
                  borderBottom: actForm.emailContent ? '1px solid #BBDEFB' : 'none',
                }}>
                  <EmailIcon sx={{ fontSize: 16, color: actForm.emailContent ? '#1565C0' : 'text.disabled' }} />
                  <Typography variant="body2" fontWeight={600}
                    sx={{ color: actForm.emailContent ? '#1565C0' : 'text.secondary', flexGrow: 1 }}>
                    {actForm.emailContent ? 'Mailinnehåll bifogat' : 'Bifoga mailinnehåll (valfritt)'}
                  </Typography>
                  {actForm.emailContent && (
                    <Tooltip title="Ta bort mailinnehåll">
                      <IconButton size="small" onClick={() => setActForm(f => ({ ...f, emailContent: '' }))}>
                        <DeleteIcon sx={{ fontSize: 15 }} />
                      </IconButton>
                    </Tooltip>
                  )}
                </Box>
                {!actForm.emailContent ? (
                  <Box sx={{ px: 2, py: 2 }}>
                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1.5, lineHeight: 1.6 }}>
                      Öppna mailet i Outlook → markera allt med <strong>Ctrl+A</strong> → kopiera med <strong>Ctrl+C</strong> → klistra in nedan med <strong>Ctrl+V</strong>
                    </Typography>
                    <TextField fullWidth multiline minRows={3} maxRows={8}
                      placeholder="Klistra in mailinnehåll här..."
                      value={actForm.emailContent}
                      onChange={e => setActForm(f => ({ ...f, emailContent: e.target.value }))}
                      variant="outlined" size="small"
                      sx={{ '& .MuiOutlinedInput-root': { fontSize: '0.82rem' } }}
                    />
                  </Box>
                ) : (
                  <Box sx={{ px: 2, py: 1.5, maxHeight: 140, overflowY: 'auto' }}>
                    <Typography variant="caption"
                      sx={{ whiteSpace: 'pre-wrap', color: 'text.secondary', fontSize: '0.78rem', lineHeight: 1.5 }}>
                      {actForm.emailContent}
                    </Typography>
                  </Box>
                )}
              </Box>
            )}

            {/* Länkad presentation — visas bara för Möte och E-post */}
            {(actForm.type === 'meeting' || actForm.type === 'email') && pitchesList.length > 0 && (
              <TextField select fullWidth label="Länkad presentation (valfritt)"
                value={actForm.pitchId || ''}
                onChange={e => {
                  const p = pitchesList.find(x => x.id === e.target.value);
                  setActForm(f => ({
                    ...f,
                    pitchId:    p?.id    || '',
                    pitchTitle: p?.title || '',
                    pitchUrl:   p ? `${window.location.origin}/p/${p.shareToken}` : '',
                  }));
                }}>
                <MenuItem value="">– Ingen presentation –</MenuItem>
                {pitchesList.map(p => (
                  <MenuItem key={p.id} value={p.id}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <SlideshowIcon sx={{ fontSize: 15, color: '#FF6B00' }} />
                      <span>{p.title || 'Namnlös pitch'}</span>
                      {p.client && <Typography variant="caption" color="text.secondary">— {p.client}</Typography>}
                    </Box>
                  </MenuItem>
                ))}
              </TextField>
            )}

            {/* Deltagare — visas bara för Möte */}
            {actForm.type === 'meeting' && (
              <Box>
                {companyContacts.length > 0 && (
                  <Box sx={{ mb: 2 }}>
                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.75 }}>
                      Välj deltagare från kundens kontakter:
                    </Typography>
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.75 }}>
                      {companyContacts.map(c => {
                        const selected = actForm.participants?.some(
                          p => (c.email && p.email === c.email) || p.name === c.name
                        );
                        return (
                          <Chip key={c.id}
                            label={c.name + (c.role ? ` (${c.role})` : '')}
                            size="small" clickable
                            onClick={() => {
                              if (selected) {
                                setActForm(f => ({
                                  ...f,
                                  participants: f.participants.filter(
                                    p => !((c.email && p.email === c.email) || p.name === c.name)
                                  ),
                                }));
                              } else {
                                setActForm(f => ({
                                  ...f,
                                  participants: [...(f.participants || []), { name: c.name, email: c.email || '' }],
                                }));
                              }
                            }}
                            variant={selected ? 'filled' : 'outlined'}
                            sx={selected
                              ? { bgcolor: '#6A1B9A', color: 'white', '& .MuiChip-label': { fontWeight: 600 } }
                              : { borderColor: '#6A1B9A', color: '#6A1B9A' }}
                          />
                        );
                      })}
                    </Box>
                  </Box>
                )}
                <TextField fullWidth
                  label="Deltagare (klistra från Outlook)"
                  placeholder='Ex: Kalle Anka <kalle@exempel.se>; Lisa Svensson <lisa@foretag.com>'
                  value={participantsPaste}
                  onChange={e => {
                    const val = e.target.value;
                    setParticipantsPaste(val);
                    const pasted = parseOutlookParticipants(val);
                    setActForm(f => {
                      const fromContacts = (f.participants || []).filter(p =>
                        companyContacts.some(c => (c.email && c.email === p.email) || c.name === p.name)
                      );
                      const merged = [...fromContacts];
                      pasted.forEach(pp => {
                        if (!merged.some(m => (pp.email && m.email === pp.email) || m.name === pp.name)) {
                          merged.push(pp);
                        }
                      });
                      return { ...f, participants: merged };
                    });
                  }}
                  multiline minRows={1}
                  helperText="Klistra in mottagarlistan från Outlook — namn och e-poster tolkas automatiskt"
                />
                {actForm.participants?.length > 0 && (
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mt: 1 }}>
                    {actForm.participants.map((p, i) => (
                      <Chip key={i} label={p.name || p.email} title={p.email} size="small"
                        onDelete={() => {
                          const updated = actForm.participants.filter((_, j) => j !== i);
                          setActForm(f => ({ ...f, participants: updated }));
                        }}
                        sx={{ bgcolor: '#e8f4fd', color: '#1565C0', fontSize: '0.72rem' }} />
                    ))}
                  </Box>
                )}
              </Box>
            )}

            {/* Uppföljning */}
            <Box sx={{ pt: 0.5 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5 }}>
                <Box sx={{ flex: 1, height: '1px', bgcolor: 'divider' }} />
                <Typography variant="caption" color="text.secondary" fontWeight={600}
                  sx={{ textTransform: 'uppercase', letterSpacing: 0.8, fontSize: '0.68rem', px: 0.5 }}>
                  Uppföljning
                </Typography>
                <Box sx={{ flex: 1, height: '1px', bgcolor: 'divider' }} />
              </Box>
              <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 1.5 }}>
                Leadets nästa steg och uppföljning – samma som på leadkortet och i redigeringen
              </Typography>
              <UppfoljningFalt value={actUppf} onChange={p => setActUppf(u => ({ ...u, ...p }))} />
            </Box>

          </Box>
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 2.5 }}>
          {lead?.companyId && (
            <KundprojektKnapp sx={{ mr: 'auto' }}
              kund={{ id: lead.companyId, name: company?.name }}
              kontakt={actForm.contactId ? { id: actForm.contactId, name: actForm.contactName } : undefined}
              leadId={leadId}
              aktivitet={{ typ: actForm.type, leadId, aktivitetId: editingActivityId || '' }}
              uid={currentUser?.uid}
              onSparad={resultat => {
                if (!editingActivityId) kundprojektVidAktivitetRef.current.push(resultat.id);
                kundprojektSparat(resultat);
              }} />
          )}
          <Button onClick={() => setActDialog(false)} size="large">Avbryt</Button>
          <Button variant="contained" onClick={handleSaveActivity} size="large"
            disabled={actSaving || !actForm.summary.trim()}
            sx={{ bgcolor: '#FF6B00', '&:hover': { bgcolor: '#e55a00' }, px: 4 }}>
            {actSaving ? 'Sparar...' : editingActivityId ? 'Spara ändringar' : 'Spara aktivitet'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Nytt företag dialog */}
      <Dialog open={newCompanyDialog} onClose={() => setNewCompanyDialog(false)} maxWidth="xs" fullWidth>
        <DialogTitle>Nytt företag</DialogTitle>
        <DialogContent>
          <TextField autoFocus fullWidth label="Företagsnamn" size="small" sx={{ mt: 1 }}
            value={newCompanyName}
            onChange={e => setNewCompanyName(e.target.value)}
            onKeyDown={e => e.key === 'Enter' && handleCreateCompany()} />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setNewCompanyDialog(false)}>Avbryt</Button>
          <Button variant="contained" onClick={handleCreateCompany}
            disabled={!newCompanyName.trim()}
            sx={{ bgcolor: '#1a1a1a', '&:hover': { bgcolor: '#333' } }}>
            Skapa
          </Button>
        </DialogActions>
      </Dialog>

      {/* Redigera lead – dialog */}
      <Dialog open={editing} onClose={() => { setEditing(false); setEditForm(lead); }}
        maxWidth="md" fullWidth PaperProps={{ sx: { borderRadius: 3 } }}>
        <DialogTitle sx={{ pb: 1 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
            <EditIcon sx={{ color: '#FF6B00', fontSize: 28 }} />
            <Box>
              <Typography variant="h6" fontWeight="bold">Redigera lead</Typography>
              <Typography variant="caption" color="text.secondary">{lead?.title}</Typography>
            </Box>
          </Box>
        </DialogTitle>
        <Divider />
        <DialogContent sx={{ pt: 2 }}>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>

            {/* ── 1. Affären ── */}
            <Box sx={{ bgcolor: '#f8f9fa', borderRadius: 2, p: 2 }}>
              <Typography variant="caption" fontWeight="bold" color="text.secondary"
                sx={{ textTransform: 'uppercase', letterSpacing: 0.6, display: 'block', mb: 1.5 }}>
                Affären
              </Typography>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                <TextField fullWidth label="Titel *" autoFocus
                  value={editForm.title || ''}
                  onChange={e => setEditForm(f => ({ ...f, title: e.target.value }))} />
                <Box sx={{ display: 'flex', gap: 1.5, flexWrap: 'wrap', alignItems: 'flex-start' }}>
                  <Box sx={{ display: 'flex', gap: 1, flex: '2 1 240px', alignItems: 'flex-start' }}>
                    <TextField select fullWidth label="Företag"
                      value={editForm.companyId || ''}
                      onChange={e => setEditForm(f => ({ ...f, companyId: e.target.value }))}>
                      <MenuItem value="">– Inget företag –</MenuItem>
                      {companies.map(c => <MenuItem key={c.id} value={c.id}>{c.name}</MenuItem>)}
                    </TextField>
                    <Tooltip title="Skapa nytt företag">
                      <IconButton onClick={() => setNewCompanyDialog(true)}
                        sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 1, mt: 1, flexShrink: 0 }}>
                        <AddIcon />
                      </IconButton>
                    </Tooltip>
                  </Box>
                  <TextField select label="Typ" sx={{ flex: '1 1 160px' }}
                    value={editForm.type || 'produkt'}
                    onChange={e => {
                      const newType = e.target.value;
                      setEditForm(f => {
                        const mvlDept = departments.find(d => d.name?.toUpperCase().includes('MVL'));
                        const shouldTagMvl = mvlDept && (newType === 'produkt' || newType === 'kombination');
                        const deptIds = f.departmentIds || [];
                        const newDeptIds = shouldTagMvl && !deptIds.includes(mvlDept.id) ? [...deptIds, mvlDept.id] : deptIds;
                        return { ...f, type: newType, productId: '', departmentIds: newDeptIds };
                      });
                    }}>
                    {BASE_TYPES.map(t => <MenuItem key={t.key} value={t.key}>{t.label}</MenuItem>)}
                  </TextField>
                  <TextField select label="Pipeline-steg" sx={{ flex: '1 1 180px' }}
                    value={editForm.status || 'identified'}
                    onChange={e => setEditForm(f => ({ ...f, status: e.target.value }))}>
                    {STAGES.map(s => (
                      <MenuItem key={s.key} value={s.key}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: s.color, flexShrink: 0 }} />
                          {s.label}
                        </Box>
                      </MenuItem>
                    ))}
                  </TextField>
                </Box>
                {(editForm.type === 'produkt' || editForm.type === 'kombination') && (
                  <TextField select fullWidth label="Produkt"
                    value={editForm.productId || ''}
                    onChange={e => setEditForm(f => ({ ...f, productId: e.target.value }))}>
                    <MenuItem value="">– Välj produkt –</MenuItem>
                    {products.map(p => <MenuItem key={p.id} value={p.id}>{p.name}</MenuItem>)}
                  </TextField>
                )}
                <Box sx={{ display: 'flex', gap: 1.5, alignItems: 'center', flexWrap: 'wrap' }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, cursor: 'pointer',
                    px: 1.5, py: 0.875, borderRadius: 1, border: '1px solid',
                    borderColor: editForm.hasSandbox ? '#FFE082' : 'divider',
                    bgcolor: editForm.hasSandbox ? '#FFF8E1' : 'transparent',
                    userSelect: 'none',
                  }} onClick={() => setEditForm(f => ({ ...f, hasSandbox: !f.hasSandbox }))}>
                    <Box sx={{ width: 14, height: 14, borderRadius: '50%', border: '2px solid #F57C00',
                      bgcolor: editForm.hasSandbox ? '#F57C00' : 'transparent', flexShrink: 0 }} />
                    <Typography variant="caption" fontWeight={editForm.hasSandbox ? 700 : 400}
                      sx={{ color: editForm.hasSandbox ? '#E65100' : 'text.secondary', fontSize: '0.8rem' }}>
                      Sandbox-testad
                    </Typography>
                  </Box>
                  {['proposal', 'negotiation', 'pilot', 'won'].includes(editForm.status) && (
                    <TextField select size="small" label="Offert-status" sx={{ width: 180 }}
                      value={editForm.offerStatus || ''}
                      onChange={e => setEditForm(f => ({ ...f, offerStatus: e.target.value }))}>
                      <MenuItem value="">– Ej satt –</MenuItem>
                      {OFFER_STATUSES.map(o => <MenuItem key={o.key} value={o.key}>{o.label}</MenuItem>)}
                    </TextField>
                  )}
                  {['demo', 'sandbox'].includes(editForm.status) && (
                    <TextField select size="small" label="Demo-status" sx={{ width: 180 }}
                      value={editForm.demoStatus || ''}
                      onChange={e => setEditForm(f => ({
                        ...f,
                        demoStatus: e.target.value,
                        // Avbokat möte: uppföljningen flyttas till idag, eftersom ny
                        // tid behöver stämmas av med kunden innan den kan sättas.
                        ...(e.target.value === 'avbokad'
                          ? { nextFollowUp: new Date().toISOString().split('T')[0], nextFollowUpStatus: '' }
                          : {}),
                      }))}>
                      <MenuItem value="">– Ej satt –</MenuItem>
                      {DEMO_STATUSES.map(d => <MenuItem key={d.key} value={d.key}>{d.label}</MenuItem>)}
                    </TextField>
                  )}
                </Box>
                {PARTNERSHIP_TAGS.length > 0 && (
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                    <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600, textTransform: 'uppercase', letterSpacing: 0.5 }}>
                      Samarbete
                    </Typography>
                    {PARTNERSHIP_TAGS.map(t => {
                      const sel = (editForm.tags || []).includes(t.key);
                      return (
                        <Chip key={t.key} label={t.key} size="small"
                          onClick={() => setEditForm(f => {
                            const cur = f.tags || [];
                            return { ...f, tags: sel ? cur.filter(k => k !== t.key) : [...cur, t.key] };
                          })}
                          sx={{ cursor: 'pointer', fontWeight: sel ? 700 : 400,
                            bgcolor: sel ? t.bg : 'transparent',
                            color: sel ? t.color : 'text.secondary',
                            border: `1px solid ${sel ? t.color : '#ccc'}` }} />
                      );
                    })}
                  </Box>
                )}
                <Box sx={{ display: 'flex', gap: 1.5, flexWrap: 'wrap' }}>
                  <TextField select fullWidth label="Ägare" sx={{ flex: '1 1 200px' }}
                    value={editForm.ownerId || ''}
                    onChange={e => setEditForm(f => ({ ...f, ownerId: e.target.value }))}>
                    {users.map(u => (
                      <MenuItem key={u.id} value={u.id}>{u.displayName || u.email}</MenuItem>
                    ))}
                  </TextField>
                  <Autocomplete freeSolo options={SWEDISH_MUNICIPALITIES} sx={{ flex: '1 1 200px' }}
                    value={editForm.municipality || ''}
                    onChange={(_, v) => setEditForm(f => ({ ...f, municipality: v || '' }))}
                    onInputChange={(_, v) => setEditForm(f => ({ ...f, municipality: v }))}
                    renderInput={params => <TextField {...params} label="Kommun" />}
                  />
                </Box>
                {departments.length > 0 && (
                  <Box>
                    <Typography variant="caption" color="text.secondary" fontWeight="bold"
                      sx={{ textTransform: 'uppercase', letterSpacing: 0.5, display: 'block', mb: 0.75 }}>
                      Affärsområden
                    </Typography>
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.75 }}>
                      {departments.map(d => {
                        const sel = (editForm.departmentIds || []).includes(d.id);
                        return (
                          <Tooltip key={d.id} title={d.name}>
                            <Chip label={deptCode(d)} size="small"
                              onClick={() => setEditForm(f => ({
                                ...f,
                                departmentIds: sel
                                  ? f.departmentIds.filter(id => id !== d.id)
                                  : [...(f.departmentIds || []), d.id]
                              }))}
                              variant={sel ? 'filled' : 'outlined'}
                              sx={{ bgcolor: sel ? '#1a1a1a' : 'transparent',
                                color: sel ? 'white' : 'text.primary', cursor: 'pointer' }} />
                          </Tooltip>
                        );
                      })}
                    </Box>
                  </Box>
                )}
              </Box>
            </Box>

            {/* ── 2. Affärsvärde ── */}
            <Box sx={{ bgcolor: '#f0f4ff', borderRadius: 2, p: 2 }}>
              <Typography variant="caption" fontWeight="bold" color="#1565C0"
                sx={{ textTransform: 'uppercase', letterSpacing: 0.6, display: 'block', mb: 1.5 }}>
                Nästa steg, tidplan & leverans
              </Typography>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                <Box sx={{ display: 'flex', gap: 1.5, flexWrap: 'wrap' }}>
                  <TextField select label="Prismodell" sx={{ flex: '1 1 160px' }}
                    value={editForm.prismodell || ''}
                    onChange={e => setEditForm(f => ({ ...f, prismodell: e.target.value }))}>
                    <MenuItem value="">– Välj –</MenuItem>
                    {PRISMODELL.map(p => <MenuItem key={p.key} value={p.key}>{p.label}</MenuItem>)}
                  </TextField>
                </Box>
                {editForm.type !== 'konsult' && (
                <Box sx={{ display: 'flex', gap: 1.5, flexWrap: 'wrap' }}>
                  <TextField select label="Driftsättning" sx={{ flex: '1 1 160px' }}
                    value={editForm.deploymentType || ''}
                    onChange={e => setEditForm(f => ({ ...f, deploymentType: e.target.value }))}>
                    <MenuItem value="">– Välj –</MenuItem>
                    {deploymentTypes.map(d => <MenuItem key={d.key} value={d.key}>{d.label}</MenuItem>)}
                  </TextField>
                  <FormControl sx={{ flex: '2 1 200px' }}>
                    <InputLabel>Plan / Licenstyp</InputLabel>
                    <Select multiple
                      value={Array.isArray(editForm.plan) ? editForm.plan : (editForm.plan ? [editForm.plan] : [])}
                      onChange={e => setEditForm(f => ({ ...f, plan: e.target.value }))}
                      input={<OutlinedInput label="Plan / Licenstyp" />}
                      renderValue={sel => (
                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                          {sel.map(val => {
                            const p = plans.find(p => p.key === val);
                            return <Chip key={val} label={p?.label || val} size="small" />;
                          })}
                        </Box>
                      )}>
                      {plans.map(p => <MenuItem key={p.key} value={p.key}>{p.label}</MenuItem>)}
                    </Select>
                  </FormControl>
                </Box>
                )}
                <UppfoljningFalt value={uppfoljningFranLead(editForm)}
                  onChange={p => setEditForm(f => ({ ...f, ...p }))} />
                <TextField fullWidth label="Förväntat avslut" type="date"
                  InputLabelProps={{ shrink: true }} sx={{ maxWidth: 260 }}
                  value={editForm.expectedCloseDate || ''}
                  onChange={e => setEditForm(f => ({ ...f, expectedCloseDate: e.target.value }))} />
                <Box>
                  <Box onClick={() => setEkonomiOppenEdit(o => !o)}
                    sx={{ display: 'flex', alignItems: 'center', gap: 0.5, cursor: 'pointer', userSelect: 'none' }}>
                    <ExpandMoreIcon sx={{ fontSize: 18, color: '#1565C0',
                      transform: ekonomiOppenEdit ? 'none' : 'rotate(-90deg)', transition: 'transform 0.15s' }} />
                    <Typography variant="caption" fontWeight="bold" color="#1565C0">
                      Ekonomi (frivilligt)
                    </Typography>
                  </Box>
                  <Collapse in={ekonomiOppenEdit}>
                    <Box sx={{ display: 'flex', gap: 1.5, flexWrap: 'wrap', pt: 1.5 }}>
                      <TextField label="Intäkt (KSEK)" type="number" placeholder="t.ex. 500"
                        sx={{ flex: '1 1 140px' }}
                        value={editForm.estimatedValue || ''}
                        onChange={e => setEditForm(f => ({ ...f, estimatedValue: e.target.value }))} />
                      <TextField select label="Enhet" sx={{ flex: '0 1 150px' }}
                        value={editForm.revenueUnit || 'ar'}
                        onChange={e => setEditForm(f => ({ ...f, revenueUnit: e.target.value }))}>
                        {REVENUE_UNITS.map(u => <MenuItem key={u.key} value={u.key}>{u.label}</MenuItem>)}
                      </TextField>
                      <TextField label="Sannolikhet %" type="number"
                        inputProps={{ min: 0, max: 100 }} placeholder="50"
                        sx={{ flex: '0 1 140px' }}
                        value={editForm.probability || ''}
                        onChange={e => setEditForm(f => ({ ...f, probability: e.target.value }))} />
                    </Box>
                  </Collapse>
                </Box>
              </Box>
            </Box>

            {/* ── 3. Beskrivning ── */}
            <Box>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 0.5 }}>
                <Typography variant="caption" fontWeight="bold" color="text.secondary"
                  sx={{ textTransform: 'uppercase', letterSpacing: 0.6 }}>
                  Beskrivning / Bakgrund
                </Typography>
                <Tooltip title="Infoga punkt (•)">
                  <Button size="small" variant="text"
                    sx={{ fontSize: '0.72rem', color: 'text.secondary', minWidth: 0, px: 1,
                      '&:hover': { color: '#FF6B00' } }}
                    onClick={() => {
                      const el = descRef.current?.querySelector('textarea');
                      const val = editForm.description || '';
                      if (!el) { setEditForm(f => ({ ...f, description: val + (val.endsWith('\n') || !val ? '' : '\n') + '• ' })); return; }
                      const s = el.selectionStart;
                      const pre = s === 0 || val[s-1] === '\n' ? '' : '\n';
                      setEditForm(f => ({ ...f, description: val.slice(0, s) + pre + '• ' + val.slice(s) }));
                      requestAnimationFrame(() => { el.focus(); el.setSelectionRange(s + pre.length + 2, s + pre.length + 2); });
                    }}>
                    • Lista
                  </Button>
                </Tooltip>
              </Box>
              <TextField ref={descRef} fullWidth multiline rows={7}
                placeholder="Beskriv möjligheten, bakgrunden och vad kunden behöver. Använd • Lista för punkter."
                value={editForm.description || ''}
                onChange={e => setEditForm(f => ({ ...f, description: e.target.value }))} />
            </Box>

          </Box>
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 2.5 }}>
          <Button size="large" onClick={() => { setEditing(false); setEditForm(lead); }}>
            Avbryt
          </Button>
          <Button variant="contained" size="large" onClick={handleSave}
            disabled={saving || !editForm.title?.trim()}
            sx={{ bgcolor: '#FF6B00', '&:hover': { bgcolor: '#e55a00' }, px: 4 }}>
            {saving ? 'Sparar...' : 'Spara ändringar'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* PLING / KLING-KLANG celebration overlay */}
      {celebration && (
        <PlingCelebration
          type={celebration.type}
          leadTitle={celebration.leadTitle}
          companyName={celebration.companyName}
          value={celebration.value}
          onClose={() => setCelebration(null)}
        />
      )}

      {/* Stage-change prompt: vill du skicka PLING / KLING-KLANG? */}
      <Dialog open={!!plingPrompt} onClose={() => setPlingPrompt(null)} maxWidth="xs" fullWidth
        PaperProps={{ sx: { borderRadius: 3 } }}>
        <DialogTitle sx={{
          bgcolor: plingPrompt === 'kling-klang' ? '#2E7D32' : '#FF6B00',
          color: '#fff', fontWeight: 900, py: 1.5,
        }}>
          {plingPrompt === 'kling-klang' ? '🏆 KLING-KLANG!' : '🔔 PLING!'}
        </DialogTitle>
        <DialogContent sx={{ pt: 2.5 }}>
          <Typography>
            {plingPrompt === 'pling'
              ? 'Leadet är nu kvalificerat! Vill du skicka ett PLING-mail till alla medarbetare?'
              : 'Affären är vunnen! Vill du skicka ett KLING-KLANG-mail och fira med hela teamet?'}
          </Typography>
        </DialogContent>
        <DialogActions sx={{ px: 2.5, pb: 2 }}>
          <Button onClick={() => setPlingPrompt(null)} sx={{ textTransform: 'none', color: 'text.secondary' }}>
            Inte nu
          </Button>
          <Button variant="contained"
            onClick={() => { setPlingDialog(plingPrompt); setPlingPrompt(null); }}
            sx={{
              textTransform: 'none', fontWeight: 700,
              bgcolor: plingPrompt === 'kling-klang' ? '#2E7D32' : '#FF6B00',
              '&:hover': { bgcolor: plingPrompt === 'kling-klang' ? '#1B5E20' : '#e55a00' },
            }}>
            Ja, utforma mailet
          </Button>
        </DialogActions>
      </Dialog>

      {/* PLING / KLING-KLANG mail composer */}
      {plingDialog && (
        <PlingMailDialog
          open={!!plingDialog}
          onClose={handlePlingDialogClose}
          type={plingDialog}
          lead={{ ...lead, id: leadId }}
          leadId={leadId}
          companyName={company?.name || ''}
          ownerName={users.find(u => u.id === lead.ownerId)?.displayName
            || users.find(u => u.id === lead.ownerId)?.name
            || users.find(u => u.id === lead.ownerId)?.email || ''}
          deptNames={departments.filter(d => (lead.departmentIds || []).includes(d.id)).map(d => d.name)}
          allUsers={users}
          currentUser={currentUser}
          previousPlingText={previousPlingText}
        />
      )}

      {/* FLATBOX-mötesbokning i Outlook */}
      <OutlookBookingDialog
        open={outlookDialog}
        onClose={() => setOutlookDialog(false)}
        currentUser={currentUser}
        defaultCustomerName={company?.name || lead.title || ''}
        contacts={companyContacts}
        linkedContactIds={lead.contactIds || []}
        defaultContactIds={defaultContact ? [defaultContact.id] : []}
        onBooked={handleMeetingBooked}
      />

      {/* Mailtråd in i loggen — ett mail per aktivitet */}
      <EmailThreadImportDialog
        open={threadDialog}
        onClose={() => setThreadDialog(false)}
        company={company}
        contacts={companyContacts}
        currentUser={currentUser}
        userProfile={userProfile}
        onImport={handleImportThread}
      />

      {/* Offertmail till kunden — öppnas i Outlook, loggas som aktivitet */}
      <OfferMailDialog
        open={offerMailOpen}
        onClose={() => setOfferMailOpen(false)}
        company={company}
        contacts={companyContacts}
        attachments={attachments}
        productName={product?.name || ''}
        currentUser={currentUser}
        onOpened={handleOfferMailOpened}
        defaultContactId={defaultContact?.id || ''}
      />

      {/* Radera lead */}
      <Dialog open={deleteConfirm} onClose={() => setDeleteConfirm(false)} maxWidth="xs" fullWidth>
        <DialogTitle sx={{ fontWeight: 700 }}>Radera lead?</DialogTitle>
        <DialogContent>
          <Typography>
            <strong>{lead.title}</strong> och alla tillhörande aktiviteter raderas permanent.
            Det går inte att ångra.
          </Typography>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2.5 }}>
          <Button onClick={() => setDeleteConfirm(false)}>Avbryt</Button>
          <Button variant="contained" color="error"
            startIcon={<DeleteIcon />} onClick={handleDeleteLead}>
            Radera permanent
          </Button>
        </DialogActions>
      </Dialog>

      {/* Contact dialog */}
      <Dialog open={contactDialog} onClose={() => setContactDialog(false)}
        maxWidth="md" fullWidth PaperProps={{ sx: { borderRadius: 3 } }}>
        <DialogTitle sx={{ pb: 1 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
            <PersonIcon sx={{ color: '#FF6B00', fontSize: 28 }} />
            <Box>
              <Typography variant="h6" fontWeight="bold">
                {editingContactId ? 'Redigera kontakt' : 'Ny kontakt'}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                {company?.name} — fyll i det du vet, du kan komplettera senare
              </Typography>
            </Box>
          </Box>
        </DialogTitle>
        <Divider />
        <DialogContent sx={{ pt: 2 }}>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>

            {/* ── Klistra från Outlook/signatur ── */}
            {!editingContactId && (
              <Box sx={{ bgcolor: '#fff8e1', borderRadius: 2, p: 2, border: '1px solid #ffe082' }}>
                <Typography variant="caption" fontWeight="bold"
                  sx={{ color: '#E65100', display: 'block', mb: 1, textTransform: 'uppercase', letterSpacing: 0.5 }}>
                  Snabbfyll — klistra från bokningsmail eller e-postsignatur
                </Typography>
                <TextField fullWidth size="small" multiline minRows={1}
                  placeholder='Klistra in bokningsmail, Outlook-mottagare eller e-postsignatur'
                  value={contactPaste}
                  onChange={e => {
                    const val = e.target.value;
                    setContactPaste(val);
                    const booking = parseBookingEmail(val);
                    if (booking) {
                      setContactForm(f => ({
                        ...f,
                        ...(booking.contactName && !f.name ? { name: booking.contactName } : {}),
                        ...(booking.contactRole && !f.role ? { role: booking.contactRole } : {}),
                        ...(booking.contactEmail && !f.email ? { email: booking.contactEmail } : {}),
                        ...(booking.contactPhone && !f.phone ? { phone: booking.contactPhone } : {}),
                      }));
                    } else {
                      const parsed = parseContactFromPaste(val);
                      if (parsed) {
                        setContactForm(f => ({
                          ...f,
                          ...(parsed.name && !f.name ? { name: parsed.name } : {}),
                          ...(parsed.email && !f.email ? { email: parsed.email } : {}),
                          ...(parsed.phone && !f.phone ? { phone: parsed.phone } : {}),
                        }));
                      }
                    }
                  }}
                  helperText="Namn, roll, telefon och e-post fylls i automatiskt — du kan sedan justera"
                  InputProps={{ sx: { bgcolor: 'white' } }}
                />
              </Box>
            )}

            {/* ── LinkedIn (överst) ── */}
            <Box sx={{ bgcolor: 'rgba(0,119,181,0.06)', borderRadius: 2, p: 2, border: '1px solid rgba(0,119,181,0.15)' }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5 }}>
                <AutoFixHighIcon sx={{ fontSize: 15, color: '#0077b5', flexShrink: 0 }} />
                <Typography variant="caption" sx={{ color: '#0077b5', lineHeight: 1.4 }}>
                  Fyll i LinkedIn-profil och klicka ✨ för att automatiskt hämta namn, roll, e-post, plats, utbildning och mer.
                </Typography>
              </Box>
              <TextField fullWidth size="small" label="LinkedIn-URL" autoFocus
                placeholder="https://www.linkedin.com/in/..."
                value={contactForm.linkedin}
                onChange={e => { setContactForm(f => ({ ...f, linkedin: e.target.value })); setEnrichContactMsg(''); }}
                InputProps={{
                  endAdornment: contactForm.linkedin && (
                    <Tooltip title="Hämta kontaktinfo från LinkedIn">
                      <IconButton size="small" onClick={handleContactEnrich} disabled={enrichingContact}
                        sx={{ borderRadius: 1, p: '3px', color: '#0077b5', '&:hover': { bgcolor: 'rgba(0,119,181,0.1)' } }}>
                        {enrichingContact ? <CircularProgress size={14} /> : <AutoFixHighIcon sx={{ fontSize: 16 }} />}
                      </IconButton>
                    </Tooltip>
                  )
                }} />
              {enrichContactMsg && (
                <Alert severity={enrichContactMsg.startsWith('Berikade') ? 'success' : 'error'}
                  sx={{ mt: 0.75, py: 0 }} onClose={() => setEnrichContactMsg('')}>
                  <Typography variant="caption">{enrichContactMsg}</Typography>
                </Alert>
              )}
            </Box>

            {/* ── Grunduppgifter ── */}
            <Box sx={{ bgcolor: '#f8f9fa', borderRadius: 2, p: 2 }}>
              <Typography variant="caption" fontWeight="bold" color="text.secondary"
                sx={{ textTransform: 'uppercase', letterSpacing: 0.6, display: 'block', mb: 1.5 }}>
                Grunduppgifter
              </Typography>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                <TextField fullWidth size="small" label="Namn *"
                  value={contactForm.name}
                  onChange={e => setContactForm(f => ({ ...f, name: e.target.value }))} />
                <Box sx={{ display: 'flex', gap: 1.5, flexWrap: 'wrap' }}>
                  <TextField fullWidth size="small" label="Roll / Befattning"
                    placeholder="t.ex. VD, Projektchef, IT-chef"
                    value={contactForm.role}
                    onChange={e => setContactForm(f => ({ ...f, role: e.target.value }))} />
                  <TextField fullWidth size="small" label="Avdelning"
                    value={contactForm.department || ''}
                    onChange={e => setContactForm(f => ({ ...f, department: e.target.value }))} />
                </Box>
                {/* Säljroll-badges */}
                <Box>
                  <Typography variant="caption" color="text.secondary" fontWeight={600}
                    sx={{ display: 'block', mb: 0.75, textTransform: 'uppercase', letterSpacing: 0.5, fontSize: '0.62rem' }}>
                    Roll i köpprocessen
                  </Typography>
                  <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                    {SALES_ROLES.map(r => {
                      const selected = (contactForm.salesRoles || []).includes(r.key);
                      return (
                        <Tooltip key={r.key} title={r.desc} placement="top" arrow>
                          <Chip
                            label={`${r.icon} ${r.label}`}
                            size="small"
                            onClick={() => setContactForm(f => ({
                              ...f,
                              salesRoles: selected
                                ? (f.salesRoles || []).filter(k => k !== r.key)
                                : [...(f.salesRoles || []), r.key],
                            }))}
                            sx={{
                              fontWeight: 700, fontSize: '0.72rem', cursor: 'pointer',
                              bgcolor: selected ? r.bg : 'transparent',
                              color: selected ? r.color : '#aaa',
                              border: `1.5px solid ${selected ? r.color : '#ddd'}`,
                              '&:hover': { bgcolor: r.bg, color: r.color, borderColor: r.color },
                              transition: 'all 0.15s',
                            }}
                          />
                        </Tooltip>
                      );
                    })}
                  </Box>
                </Box>
                <Box sx={{ display: 'flex', gap: 1.5, flexWrap: 'wrap' }}>
                  <TextField fullWidth size="small" label="E-post" type="email"
                    placeholder="namn@foretaget.se"
                    value={contactForm.email}
                    onChange={e => setContactForm(f => ({ ...f, email: e.target.value }))} />
                  <TextField fullWidth size="small" label="Mobil"
                    placeholder="+46 70 123 45 67"
                    value={contactForm.mobile || ''}
                    onChange={e => setContactForm(f => ({ ...f, mobile: e.target.value }))} />
                  <TextField fullWidth size="small" label="Direktnummer"
                    placeholder="+46 8 123 456 78"
                    value={contactForm.phone}
                    onChange={e => setContactForm(f => ({ ...f, phone: e.target.value }))} />
                </Box>
              </Box>
            </Box>

            {/* ── LinkedIn-berikade fält ── */}
            {(contactForm.location || contactForm.summary || contactForm.currentCompany || contactForm.education || contactForm.languages) && (
              <Box sx={{ bgcolor: 'rgba(0,119,181,0.05)', borderRadius: 2, p: 2, border: '1px solid rgba(0,119,181,0.15)' }}>
                <Typography variant="caption" sx={{ color: '#0077b5', fontWeight: 700, display: 'block', mb: 1.5, textTransform: 'uppercase', letterSpacing: 0.5 }}>
                  LinkedIn-data
                </Typography>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                  {contactForm.summary && (
                    <TextField fullWidth size="small" label="LinkedIn-rubrik"
                      value={contactForm.summary}
                      onChange={e => setContactForm(f => ({ ...f, summary: e.target.value }))} />
                  )}
                  <Box sx={{ display: 'flex', gap: 1.5, flexWrap: 'wrap' }}>
                    {contactForm.location && (
                      <TextField fullWidth size="small" label="Plats / Stad"
                        value={contactForm.location}
                        onChange={e => setContactForm(f => ({ ...f, location: e.target.value }))} />
                    )}
                    {contactForm.currentCompany && (
                      <TextField fullWidth size="small" label="Nuvarande arbetsgivare"
                        value={contactForm.currentCompany}
                        onChange={e => setContactForm(f => ({ ...f, currentCompany: e.target.value }))} />
                    )}
                  </Box>
                  {contactForm.education && (
                    <TextField fullWidth size="small" label="Utbildning"
                      value={contactForm.education}
                      onChange={e => setContactForm(f => ({ ...f, education: e.target.value }))} />
                  )}
                  {contactForm.languages && (
                    <TextField fullWidth size="small" label="Språk"
                      value={contactForm.languages}
                      onChange={e => setContactForm(f => ({ ...f, languages: e.target.value }))} />
                  )}
                </Box>
              </Box>
            )}

            {/* ── Anteckningar + primär ── */}
            <TextField fullWidth size="small" label="Personliga anteckningar" multiline rows={3}
              placeholder="Intressen, preferenser, vad ni pratat om senast..."
              value={contactForm.notes}
              onChange={e => setContactForm(f => ({ ...f, notes: e.target.value }))} />

            <Box sx={{
              display: 'flex', alignItems: 'center', justifyContent: 'space-between',
              p: 1.5, borderRadius: 2, border: '1px solid',
              borderColor: contactForm.primary ? '#1565C0' : 'divider',
              bgcolor: contactForm.primary ? '#E3F2FD' : 'transparent',
              transition: 'all 0.2s',
            }}>
              <Box>
                <Typography variant="body2" fontWeight={contactForm.primary ? 700 : 400}>
                  Primär kontakt
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  Visas som huvudkontakt för detta företag
                </Typography>
              </Box>
              <Switch
                checked={!!contactForm.primary}
                onChange={e => setContactForm(f => ({ ...f, primary: e.target.checked }))}
                sx={{
                  '& .MuiSwitch-switchBase.Mui-checked': { color: '#1565C0' },
                  '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': { bgcolor: '#1565C0' },
                }}
              />
            </Box>

          </Box>
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 2.5 }}>
          <Button onClick={() => setContactDialog(false)} size="large">Avbryt</Button>
          <Button variant="contained" onClick={handleSaveContact} size="large"
            disabled={savingContact || enrichingContact || !contactForm.name.trim()}
            sx={{ bgcolor: '#FF6B00', '&:hover': { bgcolor: '#e55a00' }, px: 4 }}>
            {savingContact ? 'Sparar...' : 'Spara kontakt'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Bulk e-postimport */}
      <Dialog open={bulkDialog} onClose={() => setBulkDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1, pb: 1 }}>
          <AddIcon sx={{ color: '#FF6B00' }} />
          Klistra in e-postlista
          <IconButton size="small" onClick={() => setBulkDialog(false)} sx={{ ml: 'auto' }}>
            <DeleteIcon sx={{ fontSize: 16 }} />
          </IconButton>
        </DialogTitle>
        <DialogContent sx={{ pt: 1 }}>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
            Klistra in mottagare från Outlook (semikolonseparerat). Namn och e-post hämtas automatiskt.
          </Typography>
          <TextField
            multiline minRows={4} maxRows={10} fullWidth size="small"
            placeholder={`"david.kummel@polisen.se" <david.kummel@polisen.se>; "mauricio.caballero@polisen.se" <mauricio.caballero@polisen.se>`}
            value={bulkText}
            onChange={e => {
              setBulkText(e.target.value);
              setBulkParsed(parseOutlookEmailList(e.target.value));
            }}
            sx={{ mb: 2 }}
          />
          {bulkParsed.length > 0 && (
            <Box>
              <Typography variant="caption" color="text.secondary" fontWeight={600} sx={{ mb: 0.5, display: 'block' }}>
                {bulkParsed.length} kontakter identifierade:
              </Typography>
              <Box sx={{ maxHeight: 200, overflowY: 'auto', border: '1px solid #eee', borderRadius: 1 }}>
                {bulkParsed.map((c, i) => (
                  <Box key={i} sx={{ display: 'flex', gap: 1.5, px: 1.5, py: 0.75,
                    borderBottom: i < bulkParsed.length - 1 ? '1px solid #f0f0f0' : 'none',
                    alignItems: 'center' }}>
                    <Avatar sx={{ width: 24, height: 24, fontSize: '0.6rem', bgcolor: '#FF6B00' }}>
                      {c.name.split(' ').map(w => w[0]).join('').toUpperCase().slice(0, 2)}
                    </Avatar>
                    <Box sx={{ flex: 1, minWidth: 0 }}>
                      <Typography variant="body2" fontWeight={600} noWrap>{c.name}</Typography>
                      <Typography variant="caption" color="text.secondary" noWrap>{c.email}</Typography>
                    </Box>
                  </Box>
                ))}
              </Box>
            </Box>
          )}
          {bulkText.trim() && bulkParsed.length === 0 && (
            <Alert severity="warning" sx={{ mt: 1 }}>Inga e-postadresser hittades i texten.</Alert>
          )}
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 2 }}>
          <Button onClick={() => setBulkDialog(false)}>Avbryt</Button>
          <Button variant="contained" onClick={handleBulkAdd}
            disabled={bulkAdding || bulkParsed.length === 0}
            sx={{ bgcolor: '#FF6B00', '&:hover': { bgcolor: '#e55a00' } }}>
            {bulkAdding ? 'Lägger till...' : `Lägg till ${bulkParsed.length || ''} kontakter`}
          </Button>
        </DialogActions>
      </Dialog>

      <AterkontaktBekraftelse key={aterkontakt ? `${aterkontakt.leadId}-${aterkontakt.datum}` : 'ingen'}
        bekraftelse={aterkontakt} onAndra={andraAterkontakt} onStang={() => setAterkontakt(null)} />
    </Box>
  );
}

function formatBytes(bytes) {
  if (!bytes) return '';
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(0)} KB`;
  return `${(bytes / 1024 / 1024).toFixed(1)} MB`;
}

function fileColor(mime = '') {
  if (mime.includes('pdf'))  return '#C62828';
  if (mime.includes('word') || mime.includes('document')) return '#1565C0';
  if (mime.includes('sheet') || mime.includes('excel'))   return '#2E7D32';
  if (mime.includes('presentation') || mime.includes('powerpoint')) return '#E65100';
  if (mime.includes('image')) return '#6A1B9A';
  return '#546E7A';
}

function InfoRow({ label, value }) {
  if (!value || value === '–') return null;
  return (
    <Box>
      <Typography variant="caption" color="text.secondary">{label}</Typography>
      <Typography variant="body2">{value}</Typography>
    </Box>
  );
}
